import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Save, ArrowLeft, Trash2, Copy, RotateCw, Type, Square, Package, Link2, Undo2, Map, Satellite, ChevronUp, ChevronDown, ChevronsUp, ChevronsDown, ZoomIn, ZoomOut, Pencil, Sparkles, Loader2, Star, Eye, EyeOff, ScanEye, X, Paintbrush, Eraser, Move, ArrowRight, Triangle, Diamond, MessageCircle, Lasso, Clock, RotateCcw, User, Search, MapPin, AlignLeft, AlignCenter, AlignRight, AlignCenterVertical, AlignStartVertical, AlignEndVertical, AlignHorizontalSpaceBetween, AlignVerticalSpaceBetween } from 'lucide-react';
import api from '../utils/api';


// 카카오맵 로드 (index.html에서 프록시로 로드됨)
function loadKakaoMap() {
  return new Promise((resolve, reject) => {
    if (!window.kakao?.maps) {
      reject(new Error('카카오맵 SDK가 로드되지 않았습니다.'));
      return;
    }
    window.kakao.maps.load(() => resolve(window.kakao));
  });
}

export default function EventEditorPage() {
  const { id, shareId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSharedEdit = shareId && searchParams.get('edit') === '1';
  const [shareEditable, setShareEditable] = useState(false);
  // 보기 전용: 일반 공유 링크이거나, 편집 링크지만 편집 OFF인 경우
  const isViewOnly = shareId ? (!isSharedEdit || !shareEditable) : false;
  const isViewOnlyRef = useRef(isViewOnly);
  useEffect(() => { isViewOnlyRef.current = isViewOnly; }, [isViewOnly]);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const overlaysRef = useRef([]);

  const [project, setProject] = useState(null);
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(18);
  const [showAssetPanel, setShowAssetPanel] = useState(false);
  const [pendingAsset, setPendingAsset] = useState(null);
  const [mapError, setMapError] = useState(null);
  const [mapType, setMapType] = useState('HYBRID');
  const [mapLabels, setMapLabels] = useState(true);
  const [showAiGen, setShowAiGen] = useState(false);
  const [aiDesc, setAiDesc] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiPreview, setAiPreview] = useState(null); // { imageUrl, s3Key }
  const [showEditProject, setShowEditProject] = useState(false);
  const [showRectPanel, setShowRectPanel] = useState(false);
  const [rectForm, setRectForm] = useState({ label: '영역', widthMeter: '5', heightMeter: '3', color: '#6366f1' });
  const [pendingRect, setPendingRect] = useState(null);
  const [showLabelPanel, setShowLabelPanel] = useState(false);
  const [labelForm, setLabelForm] = useState({ label: '구역명', widthMeter: '10', heightMeter: '2', color: '#ffffff' });
  const [pendingLabel, setPendingLabel] = useState(null);
  const [favAssetIds, setFavAssetIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('layout-fav-assets') || '[]'); } catch { return []; }
  });
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [currentUser, setCurrentUser] = useState(null); // { name, profileImage }
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [previewHistory, setPreviewHistory] = useState(null); // 선택된 히스토리 상세
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showGuestNameModal, setShowGuestNameModal] = useState(false);
  const [guestName, setGuestName] = useState(() => localStorage.getItem('layout-guest-name') || '');
  const [pendingSaveCallback, setPendingSaveCallback] = useState(null);
  const [extraZoom, setExtraZoom] = useState(1);
  const EXTRA_ZOOM_STEPS = [0.15, 0.25, 0.35, 0.5, 0.65, 0.8, 1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8];
  const EZ_ONE_IDX = EXTRA_ZOOM_STEPS.indexOf(1); // 1x의 인덱스
  // 오버레이 재생성 트리거 — zoom_changed 즉시가 아니라 idle 후(ppm 안정화)에만 업데이트
  const [overlayRenderKey, setOverlayRenderKey] = useState(0);

  // 우클릭 컨텍스트 메뉴
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, itemId: null });
  const contextMenuVisibleRef = useRef(false); // 클로저 내에서 최신 visible 상태 참조
  // 크기/회전 핸들 드래그 임시 저장 (mouseup 시에만 setItems)
  const pendingResizeRef = useRef(null);
  const pendingRotationRef = useRef(null);
  // Ctrl 키 상태 (드래그 다중선택용)
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const ctrlPressedRef = useRef(false);

  // 그리기
  const [drawMode, setDrawMode] = useState(null);
  const [showDrawPanel, setShowDrawPanel] = useState(false);
  const [drawColor, setDrawColor] = useState('#ff0000');
  const [drawWidth, setDrawWidth] = useState(3);
  const [drawFill, setDrawFill] = useState(true);
  const [strokes, setStrokes] = useState([]);
  const [selectedStrokeIdx, setSelectedStrokeIdx] = useState(-1);
  const [lassoMode, setLassoMode] = useState(false);
  const [selectedStrokeIndices, setSelectedStrokeIndices] = useState(new Set());
  const [selectedItemIds, setSelectedItemIds] = useState(new Set()); // 올가미로 선택된 아이템 ID들
  const drawCanvasRef = useRef(null);
  const currentStrokeRef = useRef(null);
  const strokesRef = useRef(strokes);
  const drawModeRef = useRef(drawMode);
  const drawColorRef = useRef(drawColor);
  const drawWidthRef = useRef(drawWidth);
  const drawFillRef = useRef(drawFill);
  const selectedStrokeIdxRef = useRef(-1);
  const moveStartRef = useRef(null);
  const moveOrigPointsRef = useRef(null);
  const lassoModeRef = useRef(false);
  const selectedStrokeIndicesRef = useRef(new Set());
  const selectedItemIdsRef = useRef(new Set());
  const lassoRectRef = useRef(null); // { startX, startY, endX, endY } screen coords
  const lassoMoveStartRef = useRef(null);
  const lassoMoveOrigPointsRef = useRef(null); // Map<idx, points[]>
  const lassoMoveOrigItemsRef = useRef(null); // Map<id, {lat,lng}>

  // 로드뷰
  const [showRoadview, setShowRoadview] = useState(false);
  const [roadviewReady, setRoadviewReady] = useState(false);
  const [roadviewError, setRoadviewError] = useState(null);
  const roadviewRef = useRef(null);
  const roadviewInstanceRef = useRef(null);
  const roadviewOverlaysRef = useRef([]);

  // Undo 히스토리
  const historyRef = useRef([]);
  const MAX_HISTORY = 50;

  const pushHistory = useCallback((currentItems, currentStrokes) => {
    historyRef.current.push({
      items: JSON.parse(JSON.stringify(currentItems)),
      strokes: currentStrokes !== undefined ? JSON.parse(JSON.stringify(currentStrokes)) : null,
    });
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
  }, []);

  const undo = useCallback(() => {
    if (historyRef.current.length === 0) return;
    const prev = historyRef.current.pop();
    if (prev.items !== undefined && prev.items !== null) {
      setItems(prev.items);
    }
    if (prev.strokes !== null) {
      setStrokes(prev.strokes);
    }
    setSelectedId(null);
    setSelectedStrokeIdx(-1);
    setSelectedStrokeIndices(new Set());
  }, []);

  const toggleFav = useCallback((assetId) => {
    setFavAssetIds(prev => {
      const next = prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId];
      localStorage.setItem('layout-fav-assets', JSON.stringify(next));
      return next;
    });
  }, []);

  const token = localStorage.getItem('paper_work_token');

  const itemIdCounter = useRef(1);
  const genId = () => `item_${Date.now()}_${itemIdCounter.current++}`;

  const extraZoomRef = useRef(extraZoom);
  useEffect(() => { extraZoomRef.current = extraZoom; }, [extraZoom]);

  const ppmCacheRef = useRef(1);
  // true일 때만 overlay 재생성 시 ppm 새로 계산 (zoom 변화 시 설정)
  // items 변경만으로 overlay가 재생성될 때는 ppmCacheRef 재사용 → 크기 안정
  const ppmNeedsRefreshRef = useRef(true);
  const ppmCheckTimerRef = useRef(null);
  // 방향키 debounce: setPosition 직접 이동 후 300ms 뒤에 setItems로 sync
  const arrowKeyDebounceRef = useRef(null);
  const arrowKeyHistoryPushedRef = useRef(false);
  // renderDrawings ref: keydown 핸들러(deps 고정)에서 최신 renderDrawings 호출용
  const renderDrawingsRef = useRef(null);

  // 오버레이 클릭 시 맵 click의 선택 해제를 방지하는 플래그
  const overlayClickedRef = useRef(false);

  // ref로 최신값 참조
  const pendingAssetRef = useRef(pendingAsset);
  const pendingRectRef = useRef(pendingRect);
  const pendingLabelRef = useRef(pendingLabel);
  const selectedIdRef = useRef(selectedId);
  const itemsRef = useRef(items);
  useEffect(() => { pendingAssetRef.current = pendingAsset; }, [pendingAsset]);
  useEffect(() => { pendingRectRef.current = pendingRect; }, [pendingRect]);
  useEffect(() => { pendingLabelRef.current = pendingLabel; }, [pendingLabel]);
  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(() => { itemsRef.current = items; }, [items]);
  useEffect(() => { strokesRef.current = strokes; }, [strokes]);
  useEffect(() => { drawModeRef.current = drawMode; }, [drawMode]);
  useEffect(() => { drawColorRef.current = drawColor; }, [drawColor]);
  useEffect(() => { drawWidthRef.current = drawWidth; }, [drawWidth]);
  useEffect(() => { drawFillRef.current = drawFill; }, [drawFill]);
  useEffect(() => { selectedStrokeIdxRef.current = selectedStrokeIdx; }, [selectedStrokeIdx]);
  useEffect(() => { lassoModeRef.current = lassoMode; }, [lassoMode]);
  useEffect(() => { selectedStrokeIndicesRef.current = selectedStrokeIndices; }, [selectedStrokeIndices]);
  useEffect(() => { selectedItemIdsRef.current = selectedItemIds; }, [selectedItemIds]);
  useEffect(() => { contextMenuVisibleRef.current = contextMenu.visible; }, [contextMenu.visible]);

  // 카카오 프로젝션 기반 축척 계산 (정확) + extraZoom 반영
  const getPixelsPerMeter = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.kakao) return 1;
    const kakao = window.kakao;
    const proj = map.getProjection();
    const center = map.getCenter();
    const lat = center.getLat();
    const lng = center.getLng();
    // 1m 기준 → 1px 반올림 오차로 4↔5 진동 발생
    // 100m 기준 → 소수점 정밀도 확보, 안정적인 ppm 계산
    const dLng100 = 100 / (111320 * Math.cos(lat * Math.PI / 180));
    const eastLatLng = new kakao.maps.LatLng(lat, lng + dLng100);
    const p1 = proj.containerPointFromCoords(center);
    const p2 = proj.containerPointFromCoords(eastLatLng);
    return Math.abs(p2.x - p1.x) / 100;
  }, []);

  // 데이터 로드
  useEffect(() => {
    (async () => {
      try {
        const projRes = shareId
          ? await api.get(`/event-layout/share/${shareId}`)
          : await api.get(`/event-layout/projects/${id}`);
        const proj = projRes.data;
        setProject(proj);
        setShareEditable(!!proj.shareEditable);
        if (shareId) document.title = '핑크페이퍼 행사배치도 시뮬레이션';
        setItems((proj.items || []).map(item => ({ ...item, _id: genId(), asset: item.asset || null })));
        try { setStrokes(JSON.parse(proj.drawings || '[]')); } catch { setStrokes([]); }
        setCurrentZoom(proj.zoom || 18);

        // 에셋 로드 (관리자 또는 공유 편집)
        if (!shareId) {
          const a = await api.get('/event-layout/assets');
          setAssets(a.data);
        } else if (isSharedEdit && proj.shareEditable) {
          const a = await api.get('/event-layout/share-assets');
          setAssets(a.data);
        }
      } catch {
        alert('배치도를 불러올 수 없습니다.');
        if (!shareId) navigate('/events');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, shareId]);

  // 로그인 사용자 정보 로드
  useEffect(() => {
    if (shareId) return;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        setCurrentUser({ name: data.name, profileImage: data.profileImage });
      } catch { /* 비멤버(client)는 무시 */ }
    })();
  }, [token, shareId]);

  // 카카오맵 초기화
  useEffect(() => {
    if (!project || loading || mapInstanceRef.current) return;

    loadKakaoMap().then(kakao => {
      // 저장된 zoom 레벨로 시작 (없으면 level 3 기본값)
      // level: 1 하드코딩이었을 때 → 저장된 zoom과 달라 오버레이가 항상 wrong ppm으로 생성되는 버그
      const initialLevel = Math.max(1, Math.min(14, project.zoom || 3));
      const map = new kakao.maps.Map(mapRef.current, {
        center: new kakao.maps.LatLng(project.lat, project.lng),
        mapTypeId: kakao.maps.MapTypeId.HYBRID,
        level: initialLevel,
        minLevel: 1,
      });

      mapInstanceRef.current = map;
      setCurrentZoom(map.getLevel());

      // zoom_changed → idle: 레벨 변경 후 오버레이 재생성 (mapInitDone 전은 무시)
      // pendingZoomIdle: 빠른 연속 줌 시 마지막 idle 핸들러만 유지 (중복 재생성 방지)
      let mapInitDone = false;
      let pendingZoomIdle = null;
      kakao.maps.event.addListener(map, 'zoom_changed', () => {
        const lv = map.getLevel();
        setCurrentZoom(lv);
        if (!mapInitDone) return;
        if (pendingZoomIdle) kakao.maps.event.removeListener(map, 'idle', pendingZoomIdle);
        const onIdle = () => {
          pendingZoomIdle = null;
          kakao.maps.event.removeListener(map, 'idle', onIdle);
          ppmNeedsRefreshRef.current = true; // zoom 변화 → ppm 새로 계산 필요
          setOverlayRenderKey(k => k + 1);
        };
        pendingZoomIdle = onIdle;
        kakao.maps.event.addListener(map, 'idle', onIdle);
      });

      // 맵 생성 직후 containerPointFromCoords가 불안정 → setMapReady를 첫 idle 후로 지연
      const doMapInit = () => {
        if (mapInitDone) return;
        mapInitDone = true;
        ppmNeedsRefreshRef.current = true; // 초기 로드 시 ppm 계산
        setMapReady(true);
      };
      kakao.maps.event.addListener(map, 'idle', function onFirstMapIdle() {
        kakao.maps.event.removeListener(map, 'idle', onFirstMapIdle);
        doMapInit();
      });
      // fallback: idle가 발생하지 않는 환경을 위한 2초 후 강제 초기화
      setTimeout(doMapInit, 2000);

      // 마지막 마우스 좌표 추적 (카카오 click 이벤트의 latLng 대신 직접 계산용)
      const lastMouseRef = { x: 0, y: 0 };
      mapRef.current.addEventListener('mousemove', (e) => {
        lastMouseRef.x = e.clientX;
        lastMouseRef.y = e.clientY;
      }, true);
      mapRef.current.addEventListener('mousedown', (e) => {
        lastMouseRef.x = e.clientX;
        lastMouseRef.y = e.clientY;
      }, true);
      // 우클릭 브라우저 기본 메뉴 차단 (캡처 단계 — Kakao보다 먼저 실행)
      mapRef.current.addEventListener('contextmenu', (e) => { e.preventDefault(); }, true);

      // 맵 클릭 → 에셋/영역 배치
      kakao.maps.event.addListener(map, 'click', () => {
        if (drawModeRef.current) return;
        // 오버레이 클릭 직후면 맵 click 무시 (선택 해제 방지)
        if (overlayClickedRef.current) return;
        const pa = pendingAssetRef.current;
        const pr = pendingRectRef.current;
        const pl = pendingLabelRef.current;
        if (!pa && !pr && !pl) {
          setSelectedId(null);
          return;
        }
        if (isViewOnly) return;
        // 마우스 좌표 → 카카오맵 컨테이너 좌표 → lat/lng (CSS transform 우회)
        const mapDiv = mapRef.current;
        const rect = mapDiv.getBoundingClientRect();
        const mx = lastMouseRef.x - rect.left;
        const my = lastMouseRef.y - rect.top;
        const ez = extraZoomRef.current;
        // getBoundingClientRect는 CSS scale이 반영된 크기를 반환하므로,
        // 실제 컨테이너 내 좌표로 변환: 비율 기반
        const containerW = mapDiv.clientWidth;
        const containerH = mapDiv.clientHeight;
        const containerX = mx / rect.width * containerW;
        const containerY = my / rect.height * containerH;
        const proj = map.getProjection();
        const ll = proj.coordsFromContainerPoint(new kakao.maps.Point(containerX, containerY));
        const lat = ll.getLat(), lng = ll.getLng();

        if (pa) {
          const curItems = itemsRef.current;
          pushHistory(curItems);
          const newItem = {
            _id: `item_${Date.now()}_${itemIdCounter.current++}`,
            type: 'asset', eventLayoutAssetId: pa.id, asset: pa,
            lat, lng,
            rotation: 0, widthMeter: pa.widthMeter, heightMeter: pa.heightMeter,
            label: pa.name, color: pa.color, groupId: null, zIndex: 0,
          };
          setItems(prev => [...prev, newItem]);
          setSelectedId(newItem._id);
        } else if (pr) {
          const curItems = itemsRef.current;
          pushHistory(curItems);
          const newItem = {
            _id: `item_${Date.now()}_${itemIdCounter.current++}`,
            type: 'rect', lat, lng,
            rotation: 0, widthMeter: parseFloat(pr.widthMeter) || 5, heightMeter: parseFloat(pr.heightMeter) || 3,
            label: pr.label || '영역', color: pr.color || '#6366f1', groupId: null, zIndex: -1,
          };
          setItems(prev => [...prev, newItem]);
          setSelectedId(newItem._id);
          setPendingRect(null);
        } else if (pl) {
          const curItems = itemsRef.current;
          pushHistory(curItems);
          const newItem = {
            _id: `item_${Date.now()}_${itemIdCounter.current++}`,
            type: 'label', lat, lng,
            rotation: 0, widthMeter: parseFloat(pl.widthMeter) || 10, heightMeter: parseFloat(pl.heightMeter) || 2,
            label: pl.label || '구역명', color: pl.color || '#ffffff', fontSize: 10, groupId: null, zIndex: 10,
          };
          setItems(prev => [...prev, newItem]);
          setSelectedId(newItem._id);
          setPendingLabel(null);
        }
      });
    }).catch(err => setMapError(err.message));
  }, [project, loading, pushHistory]);

  // 그리기/올가미 모드 시 지도 드래그 비활성화
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const block = !!drawMode || lassoMode;
    map.setDraggable(!block);
    map.setZoomable(!block);
  }, [drawMode, lassoMode, mapReady]);

  // 지도 타입 변경
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.kakao) return;
    const kakao = window.kakao;
    if (mapType === 'ROADMAP') {
      map.setMapTypeId(kakao.maps.MapTypeId.ROADMAP);
    } else {
      // 위성: labels on → HYBRID, off → SKYVIEW
      map.setMapTypeId(mapLabels ? kakao.maps.MapTypeId.HYBRID : kakao.maps.MapTypeId.SKYVIEW);
    }
  }, [mapType, mapLabels]);

  // 마우스 휠: extraZoom 연동 (1x 이상 확대 + 1x 이하 축소, 카카오 레벨과 부드럽게 연결)
  useEffect(() => {
    const el = mapRef.current;
    if (!el || !mapReady) return;
    let throttled = false;
    const stepEZ = (dir) => {
      // dir: +1 = 확대, -1 = 축소
      const ez = extraZoomRef.current;
      let idx = EXTRA_ZOOM_STEPS.indexOf(ez);
      if (idx < 0) idx = EZ_ONE_IDX; // 못 찾으면 1x로
      const next = idx + dir;
      if (next < 0 || next >= EXTRA_ZOOM_STEPS.length) return false;
      setExtraZoom(EXTRA_ZOOM_STEPS[next]);
      return true;
    };
    const handler = (e) => {
      const map = mapInstanceRef.current;
      if (!map) return;
      e.preventDefault();
      e.stopPropagation();
      if (throttled) return;
      throttled = true;
      setTimeout(() => { throttled = false; }, 150);

      const level = map.getLevel();
      const ez = extraZoomRef.current;
      const isZoomIn = e.deltaY < 0;
      const isZoomOut = e.deltaY > 0;

      if (isZoomIn) {
        if (ez < 1) {
          stepEZ(1); // 축소 상태 → 1x 쪽으로
        } else if (ez === 1 && level > 1) {
          map.setLevel(level - 1); // 카카오 줌인
        } else {
          stepEZ(1); // 카카오 최대 확대 → extraZoom 증가
        }
      } else if (isZoomOut) {
        if (ez > 1) {
          stepEZ(-1); // 확대 상태 → 1x 쪽으로
        } else if (ez === 1 && level < 14) {
          map.setLevel(level + 1); // 카카오 줌아웃
        } else {
          stepEZ(-1); // 카카오 최대 축소 → extraZoom 감소
        }
      }
    };
    el.addEventListener('wheel', handler, { passive: false, capture: true });
    return () => el.removeEventListener('wheel', handler, { capture: true });
  }, [mapReady]);

  // 오버레이 엘리먼트 맵 (_id → DOM element)
  const overlayElsRef = useRef({});
  // 오버레이 객체 맵 (_id → Kakao CustomOverlay) — 드래그 중 setPosition으로 직접 이동
  const overlayObjsRef = useRef({});

  // 오버레이 렌더링 (selectedId 제외 — 선택 스타일은 별도 effect에서 처리)
  useEffect(() => {
    if (!mapReady || !mapInstanceRef.current || !window.kakao) return;
    const kakao = window.kakao;
    const map = mapInstanceRef.current;

    overlaysRef.current.forEach(o => o.setMap(null));
    overlaysRef.current = [];
    overlayElsRef.current = {};
    overlayObjsRef.current = {};

    // zoom 변화 시에만 ppm 새로 계산, items 변경 시에는 캐시 재사용 → 크기 안정
    const ppm = ppmNeedsRefreshRef.current ? getPixelsPerMeter() : ppmCacheRef.current;
    if (ppmNeedsRefreshRef.current) { ppmCacheRef.current = ppm; ppmNeedsRefreshRef.current = false; }

    items.forEach(item => {
      // 짝수 정수 픽셀로 반올림: offsetWidth * 0.5가 항상 정수 → Kakao 1px 반올림 오차 제거
      const wPx = Math.ceil((item.widthMeter || item.asset?.widthMeter || 2) * ppm / 2) * 2;
      const hPx = Math.ceil((item.heightMeter || item.asset?.heightMeter || 2) * ppm / 2) * 2;
      const rot = item.rotation || 0;

      const el = document.createElement('div');
      el.dataset.itemId = item._id;
      el.style.position = 'relative';
      el.style.cursor = isViewOnly ? 'default' : 'grab';
      el.style.userSelect = 'none';

      const labelLines = (item.label || '').split('\n');
      const makeLinesHtml = (lines, color, fs, fw, shadow) => lines.map(l => `<div style="color:${color};font-size:${fs}px;font-weight:${fw};text-shadow:${shadow};text-align:center;line-height:1.2;white-space:nowrap;margin:0;padding:0">${l || ''}</div>`).join('');
      if (item.type === 'label') {
        const fontSize = item.fontSize || 10;
        el.style.transform = rot ? `rotate(${rot}deg)` : '';
        el.innerHTML = makeLinesHtml(labelLines.length ? labelLines : ['구역명'], item.color || '#fff', fontSize, 600, '0 1px 4px rgba(0,0,0,0.8)');
      } else if (item.type === 'rect') {
        const fontSize = item.fontSize || 10;
        Object.assign(el.style, {
          width: `${wPx}px`, height: `${hPx}px`,
          boxSizing: 'border-box',
          background: (item.color || '#6366f1') + '40',
          border: `1px dashed ${item.color || '#6366f1'}`,
          borderRadius: '0',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          transform: rot ? `rotate(${rot}deg)` : '',
        });
        el.innerHTML = makeLinesHtml(labelLines, 'white', fontSize, 500, '0 1px 2px rgba(0,0,0,0.5)');
      } else {
        const asset = item.asset;
        const fontSize = item.fontSize || 10;
        Object.assign(el.style, {
          width: `${wPx}px`, height: `${hPx}px`,
          boxSizing: 'border-box',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transform: rot ? `rotate(${rot}deg)` : '',
        });
        if (asset?.imageUrl) {
          el.style.backgroundImage = `url(${asset.imageUrl})`;
          el.style.backgroundSize = 'contain';
          el.style.backgroundRepeat = 'no-repeat';
          el.style.backgroundPosition = 'center';
        } else {
          el.style.border = '1px solid rgba(255,255,255,0.3)';
          el.style.borderRadius = '3px';
          el.style.backgroundColor = item.color || asset?.color || '#6366f1';
          el.innerHTML = makeLinesHtml(labelLines.length && labelLines[0] ? labelLines : [asset?.name || ''], 'white', fontSize, 500, 'none');
        }
      }

      // 드래그 처리
      if (!isViewOnly) {
        el.addEventListener('mousedown', (e) => {
          // 우클릭 메뉴가 열려있으면 드래그 시작하지 않고 메뉴만 닫기
          if (contextMenuVisibleRef.current) {
            setContextMenu({ visible: false, x: 0, y: 0, itemId: null });
            return;
          }
          e.stopPropagation();
          overlayClickedRef.current = true;
          setTimeout(() => { overlayClickedRef.current = false; }, 300);

          // Ctrl+click: 다중 선택 토글 (lasso/draw 모드 아닐 때)
          if ((e.ctrlKey || e.metaKey) && !drawModeRef.current) {
            setSelectedId(null);
            setSelectedItemIds(prev => {
              const next = new Set(prev);
              if (next.has(item._id)) next.delete(item._id);
              else next.add(item._id);
              return next;
            });
            // 선택된 아이템이 있으면 lasso 모드 진입 (이동/삭제 가능)
            if (!lassoModeRef.current) setLassoMode(true);
            return;
          }

          // 올가미/그리기 모드에서는 캔버스가 처리
          if (lassoModeRef.current || drawModeRef.current) {
            const canvas = drawCanvasRef.current;
            if (canvas) {
              canvas.dispatchEvent(new MouseEvent('mousedown', {
                bubbles: false, cancelable: true,
                clientX: e.clientX, clientY: e.clientY,
                button: e.button, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey,
              }));
            }
            return;
          }
          setPendingAsset(null);

          // 올가미로 이미 선택된 아이템이면 선택 유지, 아니면 단일 선택
          const lassoItemSel = selectedItemIdsRef.current;
          const isLassoSelected = lassoItemSel && lassoItemSel.size > 0 && lassoItemSel.has(item._id);
          if (!isLassoSelected) {
            setSelectedId(item._id);
            setSelectedItemIds(new Set());
            // 스트로크 선택도 해제
            if (selectedStrokeIndicesRef.current.size > 0) {
              selectedStrokeIdxRef.current = -1;
              selectedStrokeIndicesRef.current = new Set();
              setSelectedStrokeIndices(new Set());
              setSelectedStrokeIdx(-1);
              setShowDrawPanel(false);
              renderDrawings();
            }
          }

          const startX = e.clientX, startY = e.clientY;
          // 올가미 다중 선택 시 모든 선택된 아이템의 시작 위치 저장
          const dragTargetIds = isLassoSelected ? new Set(lassoItemSel) : new Set([item._id]);
          const startPositions = new window.Map();
          (itemsRef.current || []).forEach(it => {
            if (dragTargetIds.has(it._id)) startPositions.set(it._id, { lat: it.lat, lng: it.lng });
          });
          // 올가미 선택된 스트로크도 함께 이동
          const lassoStrokeSel = isLassoSelected ? selectedStrokeIndicesRef.current : new Set();
          const startStrokePoints = new window.Map();
          if (lassoStrokeSel.size > 0) {
            lassoStrokeSel.forEach(idx => {
              const s = strokesRef.current[idx];
              if (s) startStrokePoints.set(idx, JSON.parse(JSON.stringify(s.points)));
            });
          }
          let moved = false;

          // 드래그 중 현재 위치 추적 (mouseup 시 setItems에 사용)
          const dragCurrentPositions = new window.Map(startPositions);
          const onMove = (me) => {
            if (!moved) {
              moved = true;
              pushHistory(itemsRef.current);
            }
            const ez = extraZoomRef.current;
            const dx = (me.clientX - startX) / ez, dy = (me.clientY - startY) / ez;
            const curPpm = getPixelsPerMeter();
            const dLatM = -(dy / curPpm);
            const dLngM = dx / curPpm;
            const dLat = dLatM / 111320;
            const dLng = dLngM / (111320 * Math.cos(item.lat * Math.PI / 180));

            // setItems 대신 Kakao overlay.setPosition() 직접 호출 — 오버레이 재생성 없음
            dragTargetIds.forEach(id => {
              const sp = startPositions.get(id);
              if (!sp) return;
              const newLat = sp.lat + dLat;
              const newLng = sp.lng + dLng;
              dragCurrentPositions.set(id, { lat: newLat, lng: newLng });
              const ov = overlayObjsRef.current[id];
              if (ov) ov.setPosition(new window.kakao.maps.LatLng(newLat, newLng));
            });
            // itemsRef도 직접 업데이트 → 캔버스 선택 박스가 드래그 위치 따라감
            itemsRef.current = itemsRef.current.map(it => {
              const pos = dragCurrentPositions.get(it._id);
              return pos ? { ...it, lat: pos.lat, lng: pos.lng } : it;
            });
            // 올가미 스트로크도 함께 이동 (캔버스 기반이므로 setStrokes 유지)
            if (startStrokePoints.size > 0) {
              setStrokes(prev => prev.map((s, i) => {
                const orig = startStrokePoints.get(i);
                if (!orig) return s;
                return { ...s, points: orig.map(p => ({ lat: p.lat + dLat, lng: p.lng + dLng })) };
              }));
            }
          };
          const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            if (moved) {
              // 드래그 완료 후 최종 위치로 items 상태 업데이트 (1회만)
              setItems(prev => prev.map(it => {
                const pos = dragCurrentPositions.get(it._id);
                return pos ? { ...it, lat: pos.lat, lng: pos.lng } : it;
              }));
            }
          };
          document.addEventListener('mousemove', onMove);
          document.addEventListener('mouseup', onUp);
        });
      }

      // 우클릭 컨텍스트 메뉴
      if (!isViewOnly) {
        el.addEventListener('contextmenu', (e) => {
          e.preventDefault();
          e.stopPropagation();
          overlayClickedRef.current = true;
          setTimeout(() => { overlayClickedRef.current = false; }, 300);
          // 다중 선택에 포함된 아이템이면 선택 유지 (정렬 버튼 사용 가능하도록)
          const multiSel = selectedItemIdsRef.current;
          if (multiSel.has(item._id) && multiSel.size > 1) {
            setSelectedId(null); // 단일 선택 해제, 다중 선택 유지
          } else {
            setSelectedId(item._id);
            setSelectedItemIds(new Set());
          }
          setContextMenu({ visible: true, x: e.clientX, y: e.clientY, itemId: item._id });
        });
      }

      overlayElsRef.current[item._id] = el;

      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(item.lat, item.lng),
        content: el,
        xAnchor: 0.5, yAnchor: 0.5,
        zIndex: (item.zIndex || 0) + 10,
      });
      overlay.setMap(map);
      overlaysRef.current.push(overlay);
      overlayObjsRef.current[item._id] = overlay;
    });
  }, [items, mapReady, overlayRenderKey, getPixelsPerMeter, isViewOnly, pushHistory]);


  // 로드뷰: 특정 좌표로 이동
  const roadviewClientRef = useRef(null);
  const moveRoadview = useCallback((lat, lng) => {
    if (!window.kakao || !roadviewInstanceRef.current) return;
    const kakao = window.kakao;
    const client = roadviewClientRef.current || new kakao.maps.RoadviewClient();
    roadviewClientRef.current = client;
    const pos = new kakao.maps.LatLng(lat, lng);

    setRoadviewError(null);
    client.getNearestPanoId(pos, 100, (panoId) => {
      if (!panoId) {
        setRoadviewError('이 위치에서 로드뷰를 사용할 수 없습니다');
        return;
      }
      setRoadviewError(null);
      roadviewInstanceRef.current.setPanoId(panoId, pos);
    });
  }, []);

  // 로드뷰 초기화
  useEffect(() => {
    if (!showRoadview || !window.kakao || !mapInstanceRef.current) return;
    const kakao = window.kakao;
    const map = mapInstanceRef.current;

    setRoadviewError(null);
    setRoadviewReady(false);

    const initRoadview = () => {
      const container = roadviewRef.current;
      if (!container) return;

      const rv = new kakao.maps.Roadview(container);
      const client = new kakao.maps.RoadviewClient();
      roadviewClientRef.current = client;
      const center = map.getCenter();

      client.getNearestPanoId(center, 100, (panoId) => {
        if (!panoId) {
          setRoadviewError('이 위치에서 로드뷰를 사용할 수 없습니다');
          return;
        }
        rv.setPanoId(panoId, center);
        roadviewInstanceRef.current = rv;

        kakao.maps.event.addListener(rv, 'init', () => {
          setRoadviewReady(true);
        });
      });
    };

    // 지도 클릭 시 로드뷰 위치 이동
    const clickKey = kakao.maps.event.addListener(map, 'click', (e) => {
      // pendingAsset/Rect/Label이 없을 때만 로드뷰 이동
      if (pendingAssetRef.current || pendingRectRef.current || pendingLabelRef.current) return;
      if (roadviewInstanceRef.current) {
        moveRoadview(e.latLng.getLat(), e.latLng.getLng());
      }
    });

    const timer = setTimeout(initRoadview, 100);
    return () => {
      clearTimeout(timer);
      try { kakao.maps.event.removeListener(clickKey); } catch {}
      roadviewOverlaysRef.current.forEach(o => { try { o.setMap(null); } catch {} });
      roadviewOverlaysRef.current = [];
      // 로드뷰 인스턴스 정리 (DOM 제거 전)
      const rv = roadviewInstanceRef.current;
      if (rv) {
        try {
          // 컨테이너 비우기로 안전하게 정리
          const container = roadviewRef.current;
          if (container) container.innerHTML = '';
        } catch {}
      }
      roadviewInstanceRef.current = null;
      roadviewClientRef.current = null;
      setRoadviewReady(false);
      setRoadviewError(null);
    };
  }, [showRoadview, moveRoadview]);

  // 로드뷰 오버레이 렌더링
  useEffect(() => {
    if (!showRoadview || !roadviewReady || !roadviewInstanceRef.current || !window.kakao) return;
    const kakao = window.kakao;
    const rv = roadviewInstanceRef.current;

    // 기존 오버레이 제거
    roadviewOverlaysRef.current.forEach(o => { try { o.setMap(null); } catch {} });
    roadviewOverlaysRef.current = [];

    items.forEach(item => {
      const label = item.label || item.asset?.name || '';
      if (!label) return;

      const isRect = item.type === 'rect';
      const isLabel = item.type === 'label';
      const isAsset = item.type === 'asset';
      const color = item.color || item.asset?.color || '#6366f1';
      const hasImage = isAsset && item.asset?.imageUrl;

      // 아이콘/타입 표시
      const typeIcon = isRect ? '▢' : isLabel ? 'T' : '◆';
      const size = `${item.widthMeter || item.asset?.widthMeter || '?'}×${item.heightMeter || item.asset?.heightMeter || '?'}m`;

      const el = document.createElement('div');
      el.style.cssText = 'pointer-events:none;transform:translateY(-100%);';
      el.innerHTML = `
        <div style="
          display:flex;align-items:center;gap:6px;
          background:rgba(13,11,20,0.85);
          backdrop-filter:blur(8px);
          border:1px solid ${color}50;
          border-radius:10px;
          padding:6px 10px;
          box-shadow:0 4px 20px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05);
          white-space:nowrap;
          max-width:200px;
        ">
          ${hasImage ? `<img src="${item.asset.imageUrl}" style="width:28px;height:28px;object-fit:contain;border-radius:4px;flex-shrink:0;" />` :
            `<div style="
              width:28px;height:28px;border-radius:6px;flex-shrink:0;
              background:${color}30;border:1px solid ${color}60;
              display:flex;align-items:center;justify-content:center;
              color:${color};font-size:12px;font-weight:600;
            ">${typeIcon}</div>`
          }
          <div style="min-width:0;">
            <div style="color:#fff;font-size:11px;font-weight:600;overflow:hidden;text-overflow:ellipsis;line-height:1;">${label}</div>
            <div style="color:rgba(255,255,255,0.4);font-size:9px;line-height:1.2;">${size}</div>
          </div>
        </div>
        <div style="
          width:0;height:0;margin:0 auto;
          border-left:6px solid transparent;
          border-right:6px solid transparent;
          border-top:6px solid rgba(13,11,20,0.85);
        "></div>
      `;

      const overlay = new kakao.maps.CustomOverlay({
        position: new kakao.maps.LatLng(item.lat, item.lng),
        content: el,
        yAnchor: 1,
      });
      overlay.setMap(rv);
      roadviewOverlaysRef.current.push(overlay);
    });
  }, [showRoadview, roadviewReady, items]);

  // ─── 그리기: 좌표 변환 (extraZoom 보정) ───
  const toCanvasXY = useCallback((lat, lng) => {
    const canvas = drawCanvasRef.current;
    const map = mapInstanceRef.current;
    if (!canvas || !map || !window.kakao) return { x: 0, y: 0 };
    const proj = map.getProjection();
    const p = proj.containerPointFromCoords(new window.kakao.maps.LatLng(lat, lng));
    const ez = extraZoomRef.current;
    const cx = canvas.width / 2, cy = canvas.height / 2;
    return { x: cx + (p.x - cx) * ez, y: cy + (p.y - cy) * ez };
  }, []);

  const fromCanvasXY = useCallback((mx, my) => {
    const canvas = drawCanvasRef.current;
    const map = mapInstanceRef.current;
    if (!canvas || !map || !window.kakao) return { lat: 0, lng: 0 };
    const ez = extraZoomRef.current;
    const cx = canvas.width / 2, cy = canvas.height / 2;
    const projX = cx + (mx - cx) / ez;
    const projY = cy + (my - cy) / ez;
    const proj = map.getProjection();
    const ll = proj.coordsFromContainerPoint(new window.kakao.maps.Point(projX, projY));
    return { lat: ll.getLat(), lng: ll.getLng() };
  }, []);

  // 캔버스에 1개 획 그리기
  const drawStroke = useCallback((ctx, stroke) => {
    if (!stroke || !stroke.points || stroke.points.length < 1) return;
    const st = stroke.shapeType || 'freehand';
    ctx.strokeStyle = stroke.color;
    ctx.lineWidth = stroke.width;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.globalAlpha = 0.8;

    const fillShape = (path2d) => {
      if (!stroke.fill) return;
      ctx.fillStyle = stroke.color; ctx.globalAlpha = 0.3;
      if (path2d) ctx.fill(path2d); else ctx.fill();
      ctx.globalAlpha = 0.8;
    };

    // 두 점 → 바운딩 박스 헬퍼
    const getBounds = () => {
      if (stroke.points.length < 2) return null;
      const a = toCanvasXY(stroke.points[0].lat, stroke.points[0].lng);
      const b = toCanvasXY(stroke.points[1].lat, stroke.points[1].lng);
      return { a, b, x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(b.x - a.x), h: Math.abs(b.y - a.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    };

    if (st === 'freehand') {
      if (stroke.points.length < 2) return;
      ctx.beginPath();
      stroke.points.forEach((pt, i) => {
        const { x, y } = toCanvasXY(pt.lat, pt.lng);
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      });
      ctx.stroke();
    } else if (st === 'line') {
      const b = getBounds(); if (!b) return;
      ctx.beginPath(); ctx.moveTo(b.a.x, b.a.y); ctx.lineTo(b.b.x, b.b.y); ctx.stroke();
    } else if (st === 'arrow') {
      const b = getBounds(); if (!b) return;
      ctx.beginPath(); ctx.moveTo(b.a.x, b.a.y); ctx.lineTo(b.b.x, b.b.y); ctx.stroke();
      const angle = Math.atan2(b.b.y - b.a.y, b.b.x - b.a.x);
      const hl = Math.max(12, stroke.width * 4);
      ctx.beginPath(); ctx.moveTo(b.b.x, b.b.y);
      ctx.lineTo(b.b.x - hl * Math.cos(angle - Math.PI / 6), b.b.y - hl * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(b.b.x - hl * Math.cos(angle + Math.PI / 6), b.b.y - hl * Math.sin(angle + Math.PI / 6));
      ctx.closePath(); ctx.fillStyle = stroke.color; ctx.fill();
    } else if (st === 'dblArrow') {
      const b = getBounds(); if (!b) return;
      ctx.beginPath(); ctx.moveTo(b.a.x, b.a.y); ctx.lineTo(b.b.x, b.b.y); ctx.stroke();
      const angle = Math.atan2(b.b.y - b.a.y, b.b.x - b.a.x);
      const hl = Math.max(12, stroke.width * 4);
      ctx.fillStyle = stroke.color;
      ctx.beginPath(); ctx.moveTo(b.b.x, b.b.y);
      ctx.lineTo(b.b.x - hl * Math.cos(angle - Math.PI / 6), b.b.y - hl * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(b.b.x - hl * Math.cos(angle + Math.PI / 6), b.b.y - hl * Math.sin(angle + Math.PI / 6));
      ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(b.a.x, b.a.y);
      ctx.lineTo(b.a.x + hl * Math.cos(angle - Math.PI / 6), b.a.y + hl * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(b.a.x + hl * Math.cos(angle + Math.PI / 6), b.a.y + hl * Math.sin(angle + Math.PI / 6));
      ctx.closePath(); ctx.fill();
    } else if (st === 'rect') {
      const b = getBounds(); if (!b) return;
      if (stroke.fill) { ctx.fillStyle = stroke.color; ctx.globalAlpha = 0.3; ctx.fillRect(b.x, b.y, b.w, b.h); ctx.globalAlpha = 0.8; }
      ctx.strokeRect(b.x, b.y, b.w, b.h);
    } else if (st === 'circle') {
      const b = getBounds(); if (!b) return;
      const rx = b.w / 2, ry = b.h / 2;
      ctx.beginPath(); ctx.ellipse(b.cx, b.cy, Math.max(1, rx), Math.max(1, ry), 0, 0, Math.PI * 2);
      fillShape(); ctx.stroke();
    } else if (st === 'triangle') {
      const b = getBounds(); if (!b) return;
      ctx.beginPath(); ctx.moveTo(b.cx, b.y); ctx.lineTo(b.x + b.w, b.y + b.h); ctx.lineTo(b.x, b.y + b.h); ctx.closePath();
      fillShape(); ctx.stroke();
    } else if (st === 'diamond') {
      const b = getBounds(); if (!b) return;
      ctx.beginPath(); ctx.moveTo(b.cx, b.y); ctx.lineTo(b.x + b.w, b.cy); ctx.lineTo(b.cx, b.y + b.h); ctx.lineTo(b.x, b.cy); ctx.closePath();
      fillShape(); ctx.stroke();
    } else if (st === 'star') {
      const b = getBounds(); if (!b) return;
      const rx = b.w / 2, ry = b.h / 2;
      ctx.beginPath();
      for (let i = 0; i < 10; i++) {
        const ang = -Math.PI / 2 + i * Math.PI / 5;
        const r = i % 2 === 0 ? 1 : 0.4;
        const px = b.cx + rx * r * Math.cos(ang), py = b.cy + ry * r * Math.sin(ang);
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath(); fillShape(); ctx.stroke();
    } else if (st === 'bubble') {
      const b = getBounds(); if (!b) return;
      const bodyH = b.h * 0.75;
      const r = Math.min(10, b.w / 4, bodyH / 4);
      ctx.beginPath();
      ctx.moveTo(b.x + r, b.y);
      ctx.lineTo(b.x + b.w - r, b.y); ctx.quadraticCurveTo(b.x + b.w, b.y, b.x + b.w, b.y + r);
      ctx.lineTo(b.x + b.w, b.y + bodyH - r); ctx.quadraticCurveTo(b.x + b.w, b.y + bodyH, b.x + b.w - r, b.y + bodyH);
      ctx.lineTo(b.x + b.w * 0.35, b.y + bodyH); ctx.lineTo(b.x + b.w * 0.15, b.y + b.h); ctx.lineTo(b.x + b.w * 0.25, b.y + bodyH);
      ctx.lineTo(b.x + r, b.y + bodyH); ctx.quadraticCurveTo(b.x, b.y + bodyH, b.x, b.y + bodyH - r);
      ctx.lineTo(b.x, b.y + r); ctx.quadraticCurveTo(b.x, b.y, b.x + r, b.y);
      ctx.closePath(); fillShape(); ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }, [toCanvasXY]);

  // 캔버스 전체 다시 그리기
  const renderDrawings = useCallback(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas || !mapInstanceRef.current) return;
    const container = canvas.parentElement;
    if (!container) return;
    const w = container.clientWidth, h = container.clientHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, w, h);
    const all = [...(strokesRef.current || [])];
    if (currentStrokeRef.current) all.push(currentStrokeRef.current);
    all.filter(Boolean).forEach(s => drawStroke(ctx, s));

    // 선택된 획 하이라이트 (다중 선택 지원)
    const selIndices = selectedStrokeIndicesRef.current;
    const highlightIndices = selIndices && selIndices.size > 0
      ? selIndices
      : (selectedStrokeIdxRef.current >= 0 ? new Set([selectedStrokeIdxRef.current]) : new Set());
    highlightIndices.forEach(idx => {
      if (idx < 0 || idx >= (strokesRef.current || []).length) return;
      const s = strokesRef.current[idx];
      if (s && s.points && s.points.length > 0) {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        s.points.forEach(pt => {
          const { x, y } = toCanvasXY(pt.lat, pt.lng);
          minX = Math.min(minX, x); minY = Math.min(minY, y);
          maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
        });
        ctx.setLineDash([4, 4]); ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 1.5;
        ctx.strokeRect(minX - 6, minY - 6, maxX - minX + 12, maxY - minY + 12);
        ctx.setLineDash([]);
      }
    });
    // 올가미 선택 사각형
    const lr = lassoRectRef.current;
    if (lr) {
      ctx.setLineDash([6, 3]); ctx.strokeStyle = '#60a5fa'; ctx.lineWidth = 1;
      ctx.fillStyle = 'rgba(96, 165, 250, 0.08)';
      const x = Math.min(lr.startX, lr.endX), y = Math.min(lr.startY, lr.endY);
      const rw = Math.abs(lr.endX - lr.startX), rh = Math.abs(lr.endY - lr.startY);
      ctx.fillRect(x, y, rw, rh);
      ctx.strokeRect(x, y, rw, rh);
      ctx.setLineDash([]);
    }

    // 선택된 아이템 선택 박스 (캔버스에 직접 그려서 Kakao 오버레이 DOM 건드리지 않음)
    if (!window.kakao || !mapInstanceRef.current) return;
    const proj = mapInstanceRef.current.getProjection();
    const ppm = ppmCacheRef.current; // 오버레이 생성과 동일한 ppm 사용 → 선택박스/핸들 위치 일치
    const ez = extraZoomRef.current;
    const cw = w / 2, ch = h / 2;

    const selId = selectedIdRef.current;
    const selIds = selectedItemIdsRef.current;
    const selItemSet = new Set([...(selIds || []), ...(selId ? [selId] : [])]);
    if (selItemSet.size === 0) return;

    selItemSet.forEach(itemId => {
      const item = (itemsRef.current || []).find(i => i._id === itemId);
      if (!item) return;
      const p = proj.containerPointFromCoords(new window.kakao.maps.LatLng(item.lat, item.lng));
      const sx = cw + (p.x - cw) * ez;
      const sy = ch + (p.y - ch) * ez;
      const hw = Math.ceil((item.widthMeter || item.asset?.widthMeter || 2) * ppm / 2) * 2 * ez / 2;
      const hh = Math.ceil((item.heightMeter || item.asset?.heightMeter || 2) * ppm / 2) * 2 * ez / 2;
      const rot = (item.rotation || 0) * Math.PI / 180;
      ctx.save();
      ctx.translate(sx, sy);
      ctx.rotate(rot);
      ctx.setLineDash([5, 4]);
      ctx.strokeStyle = '#a78bfa';
      ctx.lineWidth = 1.5;
      const pad = 3;
      ctx.strokeRect(-hw - pad, -hh - pad, hw * 2 + pad * 2, hh * 2 + pad * 2);
      ctx.setLineDash([]);
      ctx.restore();

      // 리사이즈/회전 핸들 (단일 선택, 비레이블, 편집 가능)
      if (itemId === selId && !isViewOnlyRef.current && item.type !== 'label' && (selIds?.size ?? 0) === 0) {
        const cosR = Math.cos(rot), sinR = Math.sin(rot);
        // SE 코너: center + rotate((hw+pad, hh+pad))
        const seX = sx + (hw + pad) * cosR - (hh + pad) * sinR;
        const seY = sy + (hw + pad) * sinR + (hh + pad) * cosR;
        // 상단 중심: center + rotate((0, -(hh+pad+16)))
        const tcX = sx + (hh + pad + 16) * sinR;
        const tcY = sy - (hh + pad + 16) * cosR;
        ctx.save();
        ctx.fillStyle = '#a78bfa';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.fillRect(seX - 4, seY - 4, 8, 8);
        ctx.strokeRect(seX - 4, seY - 4, 8, 8);
        ctx.restore();
        ctx.save();
        ctx.fillStyle = '#a78bfa';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(tcX, tcY, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.restore();
      }
    });
  }, [drawStroke]);
  // renderDrawings 최신 버전을 ref에 동기화 (keydown 핸들러에서 deps 없이 사용)
  useEffect(() => { renderDrawingsRef.current = renderDrawings; }, [renderDrawings]);

  // 맵 이벤트로 캔버스 다시 그리기
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !window.kakao || !mapReady) return;
    const kakao = window.kakao;
    const render = () => renderDrawings();
    kakao.maps.event.addListener(map, 'center_changed', render);
    kakao.maps.event.addListener(map, 'zoom_changed', render);
    kakao.maps.event.addListener(map, 'idle', render);
    render();
    return () => {
      try { kakao.maps.event.removeListener(map, 'center_changed', render); } catch {}
      try { kakao.maps.event.removeListener(map, 'zoom_changed', render); } catch {}
      try { kakao.maps.event.removeListener(map, 'idle', render); } catch {}
    };
  }, [mapReady, renderDrawings, strokes]);

  // extraZoom 변경 시 다시 그리기
  useEffect(() => { renderDrawings(); }, [extraZoom, renderDrawings]);

  // 선택·아이템 변경 시 캔버스 다시 그리기 (선택 박스 드래그 추적 포함)
  useEffect(() => { renderDrawings(); }, [selectedId, selectedItemIds, items, renderDrawings]);

  // 그리기: 히트 테스트 (이동용)
  const findStrokeAt = useCallback((mx, my) => {
    const strks = strokesRef.current || [];
    for (let i = strks.length - 1; i >= 0; i--) {
      const s = strks[i];
      if (!s || !s.points || s.points.length === 0) continue;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      s.points.forEach(pt => {
        const { x, y } = toCanvasXY(pt.lat, pt.lng);
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
      });
      const pad = 12;
      if (mx >= minX - pad && mx <= maxX + pad && my >= minY - pad && my <= maxY + pad) return i;
    }
    return -1;
  }, [toCanvasXY]);

  // 아이템 히트 테스트 (캔버스 좌표 기준)
  const findItemAt = useCallback((mx, my) => {
    const its = itemsRef.current || [];
    const ppm = getPixelsPerMeter();
    const pad = 8; // 여유 픽셀
    for (let i = its.length - 1; i >= 0; i--) {
      const it = its[i];
      const { x: cx, y: cy } = toCanvasXY(it.lat, it.lng);
      const halfW = ((it.widthMeter || 1) * ppm * extraZoomRef.current) / 2 + pad;
      const halfH = ((it.heightMeter || 1) * ppm * extraZoomRef.current) / 2 + pad;
      if (mx >= cx - halfW && mx <= cx + halfW && my >= cy - halfH && my <= cy + halfH) return it._id;
    }
    return null;
  }, [toCanvasXY, getPixelsPerMeter]);

  // 아이템 화면 바운드 (올가미 선택용)
  const getItemScreenBounds = useCallback((item) => {
    const { x: cx, y: cy } = toCanvasXY(item.lat, item.lng);
    const ppm = getPixelsPerMeter();
    const halfW = ((item.widthMeter || 1) * ppm * extraZoomRef.current) / 2;
    const halfH = ((item.heightMeter || 1) * ppm * extraZoomRef.current) / 2;
    return { minX: cx - halfW, minY: cy - halfH, maxX: cx + halfW, maxY: cy + halfH };
  }, [toCanvasXY, getPixelsPerMeter]);

  // 그리기: 마우스 이벤트
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    const map = mapInstanceRef.current;
    if (!canvas || !map || !drawMode || !window.kakao) return;
    let isDrawing = false;

    const getMouseXY = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { mx: e.clientX - rect.left, my: e.clientY - rect.top };
    };

    const eraseAt = (mx, my) => {
      const ez = extraZoomRef.current;
      const cx = canvas.width / 2, cy = canvas.height / 2;
      const proj = map.getProjection();
      const radius = 20;
      setStrokes(prev => prev.filter(stroke =>
        !(stroke && stroke.points && stroke.points.some(pt => {
          const p = proj.containerPointFromCoords(new window.kakao.maps.LatLng(pt.lat, pt.lng));
          const sx = cx + (p.x - cx) * ez, sy = cy + (p.y - cy) * ez;
          const dx = sx - mx, dy = sy - my;
          return dx * dx + dy * dy < radius * radius;
        }))
      ));
    };

    const SHAPE_MODES = ['line', 'arrow', 'dblArrow', 'rect', 'circle', 'triangle', 'diamond', 'star', 'bubble'];

    const onDown = (e) => {
      e.preventDefault();
      isDrawing = true;
      const { mx, my } = getMouseXY(e);
      const mode = drawModeRef.current;

      if (mode === 'eraser') { eraseAt(mx, my); return; }

      const { lat, lng } = fromCanvasXY(mx, my);
      const shapeType = SHAPE_MODES.includes(mode) ? mode : 'freehand';
      currentStrokeRef.current = {
        shapeType,
        points: [{ lat, lng }],
        color: drawColorRef.current,
        width: drawWidthRef.current,
        fill: drawFillRef.current,
      };
      if (shapeType !== 'freehand') {
        currentStrokeRef.current.points.push({ lat, lng });
      }
      renderDrawings();
    };

    const onMove = (e) => {
      if (!isDrawing) return;
      const { mx, my } = getMouseXY(e);
      const mode = drawModeRef.current;

      if (mode === 'eraser') { eraseAt(mx, my); return; }

      if (!currentStrokeRef.current) return;
      const { lat, lng } = fromCanvasXY(mx, my);
      if (currentStrokeRef.current.shapeType === 'freehand') {
        currentStrokeRef.current.points.push({ lat, lng });
      } else {
        currentStrokeRef.current.points[1] = { lat, lng };
      }
      renderDrawings();
    };

    const onUp = () => {
      const finished = currentStrokeRef.current;
      currentStrokeRef.current = null;
      isDrawing = false;
      if (finished && finished.points && finished.points.length >= 2) {
        setStrokes(prev => [...prev, finished]);
      }
      renderDrawings();
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseup', onUp);
    canvas.addEventListener('mouseleave', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseup', onUp);
      canvas.removeEventListener('mouseleave', onUp);
    };
  }, [drawMode, mapReady, renderDrawings, fromCanvasXY, findStrokeAt]);

  // 올가미: 마우스 이벤트
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    const map = mapInstanceRef.current;
    if (!canvas || !map || !lassoMode || drawMode || !window.kakao) return;
    // 캔버스 크기 초기화 (findStrokeAt이 정확한 좌표를 계산할 수 있도록)
    const container = canvas.parentElement;
    if (container) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }
    renderDrawings();
    let isDragging = false;
    let didDragSelect = false; // 드래그로 범위 선택했는지 (vs 클릭)
    let isMoving = false; // 선택된 획 이동 중

    const getMouseXY = (e) => {
      const rect = canvas.getBoundingClientRect();
      return { mx: e.clientX - rect.left, my: e.clientY - rect.top };
    };

    const getStrokeBounds = (stroke) => {
      if (!stroke || !stroke.points || stroke.points.length === 0) return null;
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      stroke.points.forEach(pt => {
        const { x, y } = toCanvasXY(pt.lat, pt.lng);
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
      });
      return { minX, minY, maxX, maxY };
    };

    const onDown = (e) => {
      e.preventDefault();
      const { mx, my } = getMouseXY(e);
      const selStrokes = selectedStrokeIndicesRef.current;
      const selItems = selectedItemIdsRef.current;
      const hasSelection = selStrokes.size > 0 || selItems.size > 0;

      // 선택된 오브젝트 위를 클릭하면 이동 시작
      if (hasSelection) {
        const clickedStrokeIdx = findStrokeAt(mx, my);
        const clickedItemId = findItemAt(mx, my);
        const hitSelected = (clickedStrokeIdx >= 0 && selStrokes.has(clickedStrokeIdx))
          || (clickedItemId && selItems.has(clickedItemId));
        if (hitSelected) {
          pushHistory(itemsRef.current, strokesRef.current); // 이동 시작 전 히스토리 저장 → undo 지원
          isMoving = true;
          isDragging = true;
          const startLL = fromCanvasXY(mx, my);
          lassoMoveStartRef.current = startLL;
          // 스트로크 원본 저장
          const origStrokes = new window.Map();
          selStrokes.forEach(idx => {
            const s = strokesRef.current[idx];
            if (s) origStrokes.set(idx, JSON.parse(JSON.stringify(s.points)));
          });
          lassoMoveOrigPointsRef.current = origStrokes;
          // 아이템 원본 저장
          const origItems = new window.Map();
          selItems.forEach(id => {
            const it = itemsRef.current.find(i => i._id === id);
            if (it) origItems.set(id, { lat: it.lat, lng: it.lng });
          });
          lassoMoveOrigItemsRef.current = origItems;
          return;
        }
      }

      // 새로운 선택 시작
      isDragging = true;
      didDragSelect = false;
      isMoving = false;
      lassoRectRef.current = { startX: mx, startY: my, endX: mx, endY: my };

      // Shift/Ctrl 안 누르면 기존 선택 해제 (두 키 모두 추가 선택으로 동작)
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) {
        setSelectedStrokeIndices(new Set());
        setSelectedItemIds(new Set());
        setSelectedStrokeIdx(-1);
        setSelectedId(null);
      }
    };

    const onMove = (e) => {
      if (!isDragging) return;
      const { mx, my } = getMouseXY(e);

      if (isMoving) {
        if (!lassoMoveStartRef.current) return;
        const cur = fromCanvasXY(mx, my);
        const dLat = cur.lat - lassoMoveStartRef.current.lat;
        const dLng = cur.lng - lassoMoveStartRef.current.lng;
        // 스트로크 이동
        const origStrokes = lassoMoveOrigPointsRef.current;
        if (origStrokes && origStrokes.size > 0) {
          setStrokes(prev => prev.map((s, i) => {
            if (!origStrokes.has(i)) return s;
            return { ...s, points: origStrokes.get(i).map(p => ({ lat: p.lat + dLat, lng: p.lng + dLng })) };
          }));
        }
        // 아이템 이동
        const origItems = lassoMoveOrigItemsRef.current;
        if (origItems && origItems.size > 0) {
          setItems(prev => prev.map(it => {
            if (!origItems.has(it._id)) return it;
            const orig = origItems.get(it._id);
            return { ...it, lat: orig.lat + dLat, lng: orig.lng + dLng };
          }));
        }
        return;
      }

      // 올가미 드래그
      const lr = lassoRectRef.current;
      if (lr) {
        lr.endX = mx; lr.endY = my;
        const dx = Math.abs(lr.endX - lr.startX), dy = Math.abs(lr.endY - lr.startY);
        if (dx > 5 || dy > 5) didDragSelect = true;
      }
      renderDrawings();
    };

    const onUp = (e) => {
      if (!isDragging) { isDragging = false; return; }
      isDragging = false;

      if (isMoving) {
        lassoMoveStartRef.current = null;
        lassoMoveOrigPointsRef.current = null;
        lassoMoveOrigItemsRef.current = null;
        isMoving = false;
        return;
      }

      const lr = lassoRectRef.current;
      lassoRectRef.current = null;

      if (didDragSelect && lr) {
        // 사각형 안에 포함된 오브젝트 선택 (스트로크 + 아이템)
        const rx1 = Math.min(lr.startX, lr.endX), ry1 = Math.min(lr.startY, lr.endY);
        const rx2 = Math.max(lr.startX, lr.endX), ry2 = Math.max(lr.startY, lr.endY);
        const addMode = e.shiftKey || e.ctrlKey || e.metaKey;
        const newStrokeSel = new Set(addMode ? selectedStrokeIndicesRef.current : []);
        const newItemSel = new Set(addMode ? selectedItemIdsRef.current : []);
        // 스트로크
        (strokesRef.current || []).forEach((s, i) => {
          const b = getStrokeBounds(s);
          if (!b) return;
          if (b.maxX >= rx1 && b.minX <= rx2 && b.maxY >= ry1 && b.minY <= ry2) newStrokeSel.add(i);
        });
        // 아이템
        (itemsRef.current || []).forEach(it => {
          const b = getItemScreenBounds(it);
          if (b.maxX >= rx1 && b.minX <= rx2 && b.maxY >= ry1 && b.minY <= ry2) newItemSel.add(it._id);
        });
        selectedStrokeIndicesRef.current = newStrokeSel;
        selectedItemIdsRef.current = newItemSel;
        setSelectedStrokeIndices(newStrokeSel);
        setSelectedItemIds(newItemSel);
        setSelectedId(null);
      } else {
        // 단일 클릭
        const { mx, my } = getMouseXY(e);
        const strokeIdx = findStrokeAt(mx, my);
        const itemId = findItemAt(mx, my);
        const singleAddMode = e.shiftKey || e.ctrlKey || e.metaKey;
        const newStrokeSel = new Set(singleAddMode ? selectedStrokeIndicesRef.current : []);
        const newItemSel = new Set(singleAddMode ? selectedItemIdsRef.current : []);
        if (strokeIdx >= 0) {
          if (newStrokeSel.has(strokeIdx)) newStrokeSel.delete(strokeIdx); else newStrokeSel.add(strokeIdx);
        } else if (itemId) {
          if (newItemSel.has(itemId)) newItemSel.delete(itemId); else newItemSel.add(itemId);
        } else if (!singleAddMode) {
          // 빈 곳 클릭 → 전체 해제
        }
        selectedStrokeIndicesRef.current = newStrokeSel;
        selectedItemIdsRef.current = newItemSel;
        setSelectedStrokeIndices(newStrokeSel);
        setSelectedItemIds(newItemSel);
        setSelectedId(null);
      }
      renderDrawings();
    };

    canvas.addEventListener('mousedown', onDown);
    // onMove/onUp은 document 레벨 — 오버레이 위에서 드래그해도 이동 이벤트가 끊기지 않음
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [lassoMode, drawMode, mapReady, renderDrawings, fromCanvasXY, findStrokeAt, findItemAt, getItemScreenBounds, toCanvasXY, pushHistory]);

  // 캔버스 핸들 인터랙션 (단일 선택 시: 리사이즈/회전 핸들 클릭 처리 + 패스쓰루)
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas || !mapInstanceRef.current || !selectedId || isViewOnly || drawMode || lassoMode) return;
    const HANDLE_HIT_R = 10;
    const getHandlePositions = () => {
      const item = itemsRef.current.find(i => i._id === selectedIdRef.current);
      if (!item || item.type === 'label') return null;
      const ez = extraZoomRef.current;
      const { x: sx, y: sy } = toCanvasXY(item.lat, item.lng);
      const hw = Math.ceil((item.widthMeter || item.asset?.widthMeter || 2) * ppmCacheRef.current / 2) * 2 * ez / 2;
      const hh = Math.ceil((item.heightMeter || item.asset?.heightMeter || 2) * ppmCacheRef.current / 2) * 2 * ez / 2;
      const rot = (item.rotation || 0) * Math.PI / 180;
      const cosR = Math.cos(rot), sinR = Math.sin(rot);
      const pad = 3;
      const seX = sx + (hw + pad) * cosR - (hh + pad) * sinR;
      const seY = sy + (hw + pad) * sinR + (hh + pad) * cosR;
      const tcX = sx + (hh + pad + 16) * sinR;
      const tcY = sy - (hh + pad + 16) * cosR;
      return { seX, seY, tcX, tcY, sx, sy };
    };
    const onDown = (e) => {
      if (e.button !== 0) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const pos = getHandlePositions();
      if (!pos) {
        // 핸들 없음(label 등) → 패스쓰루
        canvas.style.pointerEvents = 'none';
        const below = document.elementFromPoint(e.clientX, e.clientY);
        canvas.style.pointerEvents = 'auto';
        if (below && below !== canvas) below.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true, clientX: e.clientX, clientY: e.clientY, button: e.button, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, metaKey: e.metaKey }));
        return;
      }
      const { seX, seY, tcX, tcY, sx, sy } = pos;
      const dResize = Math.hypot(mx - seX, my - seY);
      const dRotate = Math.hypot(mx - tcX, my - tcY);
      if (dResize <= HANDLE_HIT_R) {
        e.stopPropagation(); e.preventDefault();
        const curItem = itemsRef.current.find(i => i._id === selectedIdRef.current);
        if (!curItem) return;
        const startMouseX = e.clientX, startMouseY = e.clientY;
        const startW = curItem.widthMeter || 1, startH = curItem.heightMeter || 1;
        const startLat = curItem.lat, startLng = curItem.lng;
        const rot = ((curItem.rotation || 0) * Math.PI) / 180;
        const ppm0 = ppmCacheRef.current;
        const ez0 = extraZoomRef.current;
        const startWpx = Math.ceil(startW * ppm0 / 2) * 2;
        const startHpx = Math.ceil(startH * ppm0 / 2) * 2;
        const cosR = Math.cos(rot), sinR = Math.sin(rot);
        // NW 코너 viewport 위치
        const mapRect = canvas.getBoundingClientRect();
        const nwCX = sx - (startWpx / 2) * ez0 * cosR + (startHpx / 2) * ez0 * sinR;
        const nwCY = sy - (startWpx / 2) * ez0 * sinR - (startHpx / 2) * ez0 * cosR;
        const nwVX = nwCX + mapRect.left, nwVY = nwCY + mapRect.top;
        const previewDiv = document.createElement('div');
        previewDiv.style.cssText = `position:fixed;left:${nwVX}px;top:${nwVY}px;width:${startWpx * ez0}px;height:${startHpx * ez0}px;border:2px dashed #ef4444;box-sizing:border-box;transform-origin:0 0;transform:rotate(${curItem.rotation || 0}deg);pointer-events:none;z-index:9999;`;
        document.body.appendChild(previewDiv);
        const curEl = overlayElsRef.current[curItem._id];
        if (curEl) curEl.style.opacity = '0';
        let didMove = false;
        const onMove = (me) => {
          if (!didMove) { didMove = true; pushHistory(itemsRef.current); }
          const dx = (me.clientX - startMouseX) / ez0, dy = (me.clientY - startMouseY) / ez0;
          const cosN = Math.cos(-rot), sinN = Math.sin(-rot);
          const dxR = dx * cosN - dy * sinN, dyR = dx * sinN + dy * cosN;
          const newW = Math.max(0.5, startW + dxR / ppm0);
          const newH = Math.max(0.5, startH + dyR / ppm0);
          previewDiv.style.width = `${Math.ceil(newW * ppm0 / 2) * 2 * ez0}px`;
          previewDiv.style.height = `${Math.ceil(newH * ppm0 / 2) * 2 * ez0}px`;
          pendingResizeRef.current = { id: curItem._id, widthMeter: newW, heightMeter: newH };
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          if (previewDiv.parentNode) previewDiv.parentNode.removeChild(previewDiv);
          if (curEl) curEl.style.opacity = '';
          if (pendingResizeRef.current) {
            const { id: pid, widthMeter, heightMeter } = pendingResizeRef.current;
            pendingResizeRef.current = null;
            if (mapInstanceRef.current && window.kakao) {
              const proj = mapInstanceRef.current.getProjection();
              const startPt = proj.containerPointFromCoords(new window.kakao.maps.LatLng(startLat, startLng));
              const sHW = startWpx / 2, sHH = startHpx / 2;
              const nHW = Math.ceil(widthMeter * ppm0 / 2) * 2 / 2;
              const nHH = Math.ceil(heightMeter * ppm0 / 2) * 2 / 2;
              const nwNX = startPt.x + (-sHW) * cosR - (-sHH) * sinR;
              const nwNY = startPt.y + (-sHW) * sinR + (-sHH) * cosR;
              const newCX = nwNX + nHW * cosR - nHH * sinR;
              const newCY = nwNY + nHW * sinR + nHH * cosR;
              const newLL = proj.coordsFromContainerPoint(new window.kakao.maps.Point(newCX, newCY));
              setItems(prev => prev.map(it => it._id === pid
                ? { ...it, widthMeter, heightMeter, lat: newLL.getLat(), lng: newLL.getLng() } : it));
            }
          }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      } else if (dRotate <= HANDLE_HIT_R) {
        e.stopPropagation(); e.preventDefault();
        const curItem = itemsRef.current.find(i => i._id === selectedIdRef.current);
        if (!curItem) return;
        const curEl = overlayElsRef.current[curItem._id];
        const mapRect = canvas.getBoundingClientRect();
        const cx0 = sx + mapRect.left, cy0 = sy + mapRect.top;
        const startAngle = Math.atan2(e.clientY - cy0, e.clientX - cx0);
        const startRot = curItem?.rotation || 0;
        let didMove = false;
        const onMove = (me) => {
          if (!didMove) { didMove = true; pushHistory(itemsRef.current); }
          if (!curEl) return;
          const angle = Math.atan2(me.clientY - cy0, me.clientX - cx0);
          const delta = (angle - startAngle) * 180 / Math.PI;
          const newRot = ((startRot + delta) % 360 + 360) % 360;
          curEl.style.transform = `rotate(${newRot}deg)`;
          pendingRotationRef.current = { id: curItem._id, rotation: newRot };
        };
        const onUp = () => {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          if (pendingRotationRef.current) {
            const { id: pid, rotation } = pendingRotationRef.current;
            setItems(prev => prev.map(it => it._id === pid ? { ...it, rotation } : it));
            pendingRotationRef.current = null;
          }
        };
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      } else {
        // 핸들 히트 없음 → 이벤트를 아래 요소로 패스쓰루
        canvas.style.pointerEvents = 'none';
        const below = document.elementFromPoint(e.clientX, e.clientY);
        canvas.style.pointerEvents = 'auto';
        if (below && below !== canvas) {
          below.dispatchEvent(new MouseEvent('mousedown', {
            bubbles: true, cancelable: true,
            clientX: e.clientX, clientY: e.clientY,
            button: e.button, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey, metaKey: e.metaKey,
          }));
        }
      }
    };
    canvas.addEventListener('mousedown', onDown);
    return () => canvas.removeEventListener('mousedown', onDown);
  }, [selectedId, isViewOnly, drawMode, lassoMode, toCanvasXY, pushHistory]);

  // 저장
  const [lastSaved, setLastSaved] = useState(null);
  const savePayload = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return null;
    const center = map.getCenter();
    return {
      lat: center.getLat(), lng: center.getLng(), zoom: map.getLevel(),
      items: itemsRef.current.map(({ _id, asset, createdAt, updatedAt, id: _, ...rest }) => ({
        type: rest.type || 'asset', eventLayoutAssetId: rest.eventLayoutAssetId || null,
        lat: rest.lat, lng: rest.lng, rotation: rest.rotation || 0,
        widthMeter: rest.widthMeter || null, heightMeter: rest.heightMeter || null,
        label: rest.label || null, color: rest.color || null,
        fontSize: rest.fontSize || null,
        groupId: rest.groupId || null, zIndex: rest.zIndex || 0,
      })),
      drawings: strokesRef.current,
    };
  }, []);

  const [saveComment, setSaveComment] = useState('');
  const [showSaveComment, setShowSaveComment] = useState(false);
  // 프리뷰 시 원본 데이터 백업
  const previewBackupRef = useRef(null);

  const doSave = async (silent = false, name, comment) => {
    const payload = savePayload();
    if (!payload || !project) return;
    if (comment) payload.comment = comment;
    if (silent) payload.skipHistory = true;
    setSaving(true);
    try {
      if (shareId) {
        payload.guestName = name || guestName || '익명';
        await api.put(`/event-layout/share/${shareId}/items`, payload);
      } else {
        await api.put(`/event-layout/projects/${project.id}/items`, payload);
      }
      setLastSaved(new Date());
      if (showHistory) loadHistory();
      if (!silent) alert('저장되었습니다.');
    } catch {
      if (!silent) alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async (silent = false) => {
    if (silent) { await doSave(true); return; }
    // 수동 저장: 코멘트(+이름) 모달 표시
    setSaveComment('');
    setShowGuestNameModal(true);
  };

  // 히스토리 조회
  const loadHistory = async () => {
    if (!project && !shareId) return;
    setHistoryLoading(true);
    try {
      const url = shareId
        ? `/event-layout/share/${shareId}/history`
        : `/event-layout/projects/${project.id}/history`;
      const { data } = await api.get(url);
      setHistoryList(data);
    } catch { setHistoryList([]); }
    finally { setHistoryLoading(false); }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    setPreviewHistory(null);
    // 프리뷰 복원
    if (!next && previewBackupRef.current) {
      setItems(previewBackupRef.current.items);
      setStrokes(previewBackupRef.current.strokes);
      previewBackupRef.current = null;
    }
    if (next) loadHistory();
  };

  // 히스토리 상세 로드 + 지도에 프리뷰 표시
  const loadHistoryDetail = async (historyId) => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/event-layout/history/${historyId}`);
      setPreviewHistory(data);
      // 현재 상태 백업 (최초 1회만)
      if (!previewBackupRef.current) {
        previewBackupRef.current = {
          items: [...itemsRef.current],
          strokes: [...strokesRef.current],
        };
      }
      // 히스토리 데이터로 지도 표시
      const hItems = JSON.parse(data.items || '[]').map(it => ({ ...it, _id: it._id || `h_${it.id || Math.random()}` }));
      const hStrokes = JSON.parse(data.drawings || '[]');
      setItems(hItems);
      setStrokes(hStrokes);
    } catch { alert('히스토리 로드 실패'); }
    finally { setPreviewLoading(false); }
  };

  // 프리뷰 → 목록으로 돌아갈 때 원본 복원
  const exitPreview = () => {
    setPreviewHistory(null);
    if (previewBackupRef.current) {
      setItems(previewBackupRef.current.items);
      setStrokes(previewBackupRef.current.strokes);
      previewBackupRef.current = null;
    }
  };

  // 히스토리 삭제
  const deleteHistory = async (historyId) => {
    if (!window.confirm('이 기록을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/event-layout/history/${historyId}`);
      loadHistory();
      if (previewHistory?.id === historyId) exitPreview();
    } catch (err) { alert('삭제 실패: ' + (err?.response?.status || '') + ' ' + (err?.response?.data?.message || err?.message || '')); }
  };

  const deleteAllHistory = async () => {
    if (!window.confirm('모든 히스토리를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.')) return;
    try {
      await api.delete(`/event-layout/projects/${project.id}/history`);
      setHistoryList([]);
      exitPreview();
    } catch (err) { alert('삭제 실패: ' + (err?.response?.status || '') + ' ' + (err?.response?.data?.message || err?.message || '')); }
  };

  // 복원
  const restoreHistory = async (historyId) => {
    if (!window.confirm('이 시점으로 복원하시겠습니까?\n현재 상태는 자동으로 백업됩니다.')) return;
    try {
      const url = shareId
        ? `/event-layout/share/${shareId}/restore/${historyId}`
        : `/event-layout/projects/${project.id}/restore/${historyId}`;
      const body = shareId ? { guestName: guestName || '익명' } : {};
      const { data } = await api.post(url, body);
      // 복원된 데이터 반영
      setItems((data.items || []).map(it => ({ ...it, _id: it._id || `item_${it.id}_${Date.now()}` })));
      try { setStrokes(JSON.parse(data.drawings || '[]')); } catch { setStrokes([]); }
      setProject(data);
      previewBackupRef.current = null;
      setPreviewHistory(null);
      setShowHistory(false);
      alert('복원되었습니다.');
    } catch {
      alert('복원 중 오류가 발생했습니다.');
    }
  };


  // 프로젝트 정보 수정
  const openEditProject = () => {
    setEditForm({ title: project?.title || '', description: project?.description || '' });
    setShowEditProject(true);
  };
  const handleEditProject = async (e) => {
    e.preventDefault();
    if (!editForm.title) return;
    try {
      await api.put(`/event-layout/projects/${project.id}`, editForm);
      setProject(prev => ({ ...prev, ...editForm }));
      setShowEditProject(false);
    } catch {
      alert('수정 중 오류가 발생했습니다.');
    }
  };

  // 아이템 조작
  const selectedItem = items.find(i => i._id === selectedId);
  const selectedStroke = selectedStrokeIdx >= 0 ? strokes[selectedStrokeIdx] : null;
  const hasSelection = !!selectedItem || !!selectedStroke;
  const updateSelected = (u) => {
    pushHistory(items);
    setItems(prev => prev.map(it => it._id === selectedId ? { ...it, ...u } : it));
  };
  const deleteSelected = () => {
    if (selectedStroke) {
      pushHistory(items, strokes);
      setStrokes(prev => prev.filter((_, i) => i !== selectedStrokeIdx));
      setSelectedStrokeIdx(-1);
      setSelectedStrokeIndices(new Set());
      return;
    }
    pushHistory(items, strokes);
    setItems(prev => prev.filter(it => it._id !== selectedId));
    setSelectedId(null);
  };
  const duplicateSelected = () => {
    if (selectedStroke) {
      pushHistory(items, strokes);
      const dup = JSON.parse(JSON.stringify(selectedStroke));
      dup.points = dup.points.map(p => ({ lat: p.lat + 0.00003, lng: p.lng + 0.00003 }));
      setStrokes(prev => [...prev, dup]);
      setSelectedStrokeIdx(strokes.length);
      setSelectedStrokeIndices(new Set([strokes.length]));
      return;
    }
    if (!selectedItem) return;
    pushHistory(items, strokes);
    const n = { ...selectedItem, _id: genId(), lat: selectedItem.lat + 0.00003, lng: selectedItem.lng + 0.00003 };
    setItems(prev => [...prev, n]); setSelectedId(n._id);
  };
  const rotateSelected = (deg) => {
    if (selectedStroke) {
      pushHistory(items, strokes);
      const pts = selectedStroke.points;
      if (!pts || pts.length === 0) return;
      const cLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
      const cLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
      const rad = (deg * Math.PI) / 180;
      const cos = Math.cos(rad), sin = Math.sin(rad);
      const rotated = pts.map(p => {
        const dLat = p.lat - cLat, dLng = p.lng - cLng;
        return { lat: cLat + dLat * cos - dLng * sin, lng: cLng + dLat * sin + dLng * cos };
      });
      setStrokes(prev => prev.map((s, i) => i !== selectedStrokeIdx ? s : { ...s, points: rotated }));
      return;
    }
    if (selectedItem) updateSelected({ rotation: ((selectedItem.rotation || 0) + deg) % 360 });
  };

  // Z-Index 조작 (버튼 방식)
  const moveZIndex = (direction) => {
    if (!selectedItem) return;
    pushHistory(items);
    const sorted = [...items].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
    const zValues = sorted.map(i => i.zIndex || 0);
    const curZ = selectedItem.zIndex || 0;

    let newZ = curZ;
    if (direction === 'front') {
      newZ = Math.max(...zValues) + 1;
    } else if (direction === 'back') {
      newZ = Math.min(...zValues) - 1;
    } else if (direction === 'up') {
      const above = zValues.filter(z => z > curZ);
      newZ = above.length > 0 ? Math.min(...above) + 1 : curZ + 1;
    } else if (direction === 'down') {
      const below = zValues.filter(z => z < curZ);
      newZ = below.length > 0 ? Math.max(...below) - 1 : curZ - 1;
    }
    setItems(prev => prev.map(it => it._id === selectedId ? { ...it, zIndex: newZ } : it));
  };

  const toggleRectPanel = () => {
    setShowRectPanel(p => !p);
    setPendingRect(null);
    setPendingAsset(null);
    setPendingLabel(null);
    setShowAssetPanel(false);
    setShowLabelPanel(false);
    setDrawMode(null);
    setShowDrawPanel(false);
  };
  const toggleLabelPanel = () => {
    setShowLabelPanel(p => !p);
    setPendingLabel(null);
    setPendingAsset(null);
    setPendingRect(null);
    setShowAssetPanel(false);
    setShowRectPanel(false);
    setDrawMode(null);
    setShowDrawPanel(false);
  };
  // 일반 모드: 그리기 오브젝트 클릭 선택 + 드래그 이동
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    const map = mapInstanceRef.current;
    if (!canvas || !map || drawMode || lassoMode || !mapReady) return;
    // 스트로크 없으면 캔버스 비활성
    if (!(strokesRef.current?.length)) {
      canvas.style.pointerEvents = 'none';
      return;
    }
    canvas.style.pointerEvents = 'auto';
    canvas.style.cursor = 'default';
    // 캔버스 크기 보장
    const container = canvas.parentElement;
    if (container && (canvas.width === 0 || canvas.height === 0)) {
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    }

    let isDragging = false;
    let dragIdx = -1;

    const onDown = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      const idx = findStrokeAt(mx, my);
      if (idx >= 0) {
        e.preventDefault();
        e.stopPropagation();
        selectedStrokeIdxRef.current = idx;
        selectedStrokeIndicesRef.current = new Set([idx]);
        setSelectedStrokeIdx(idx);
        setSelectedStrokeIndices(new Set([idx]));
        setSelectedId(null);
        setShowDrawPanel(true);
        isDragging = true;
        dragIdx = idx;
        moveStartRef.current = fromCanvasXY(mx, my);
        moveOrigPointsRef.current = JSON.parse(JSON.stringify(strokesRef.current[idx].points));
        renderDrawings();
      } else {
        // 스트로크 아닌 곳 → 이벤트를 아래 요소로 통과
        canvas.style.pointerEvents = 'none';
        const below = document.elementFromPoint(e.clientX, e.clientY);
        canvas.style.pointerEvents = 'auto';
        if (below && below !== canvas) {
          below.dispatchEvent(new MouseEvent('mousedown', {
            bubbles: true, cancelable: true,
            clientX: e.clientX, clientY: e.clientY,
            button: e.button, ctrlKey: e.ctrlKey, shiftKey: e.shiftKey,
          }));
        }
        // 스트로크 선택 해제 + 사이드바 닫기
        if (selectedStrokeIndicesRef.current.size > 0 || selectedStrokeIdxRef.current >= 0) {
          selectedStrokeIdxRef.current = -1;
          selectedStrokeIndicesRef.current = new Set();
          setSelectedStrokeIndices(new Set());
          setSelectedStrokeIdx(-1);
          setShowDrawPanel(false);
          setDrawMode(null);
          renderDrawings();
        }
      }
    };

    const onMove = (e) => {
      if (!isDragging || dragIdx < 0) return;
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left, my = e.clientY - rect.top;
      if (!moveStartRef.current || !moveOrigPointsRef.current) return;
      const cur = fromCanvasXY(mx, my);
      const dLat = cur.lat - moveStartRef.current.lat;
      const dLng = cur.lng - moveStartRef.current.lng;
      setStrokes(prev => prev.map((s, i) => i !== dragIdx ? s : {
        ...s, points: moveOrigPointsRef.current.map(p => ({ lat: p.lat + dLat, lng: p.lng + dLng })),
      }));
    };

    const onUp = () => {
      isDragging = false;
      dragIdx = -1;
      moveStartRef.current = null;
      moveOrigPointsRef.current = null;
    };

    // 마우스 휠은 캔버스를 통과시켜 지도 줌 작동
    const onWheel = (e) => {
      canvas.style.pointerEvents = 'none';
      const below = document.elementFromPoint(e.clientX, e.clientY);
      canvas.style.pointerEvents = 'auto';
      if (below && below !== canvas) {
        below.dispatchEvent(new WheelEvent('wheel', {
          bubbles: true, cancelable: true,
          clientX: e.clientX, clientY: e.clientY,
          deltaX: e.deltaX, deltaY: e.deltaY, deltaMode: e.deltaMode,
        }));
      }
    };

    canvas.addEventListener('mousedown', onDown);
    canvas.addEventListener('wheel', onWheel, { passive: true });
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      canvas.removeEventListener('wheel', onWheel);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, [drawMode, lassoMode, mapReady, strokes.length, findStrokeAt, fromCanvasXY, renderDrawings]);

  const doPlaceSearch = useCallback(async () => {
    const q = searchQuery.trim();
    if (!q) return;
    try {
      const { data } = await api.get('/event-layout/search-place', { params: { q } });
      if (data && data.length > 0) {
        setSearchResults(data);
      } else {
        setSearchResults([]);
        alert('검색 결과가 없습니다.');
      }
    } catch {
      alert('검색 중 오류가 발생했습니다.');
    }
  }, [searchQuery]);

  const toggleDrawPanel = () => {
    setShowDrawPanel(p => {
      if (!p) setDrawMode('pen');
      else setDrawMode(null);
      return !p;
    });
    setLassoMode(false);
    setSelectedStrokeIndices(new Set());
    setPendingAsset(null);
    setPendingRect(null);
    setPendingLabel(null);
    setShowAssetPanel(false);
    setShowRectPanel(false);
    setShowLabelPanel(false);
  };

  const toggleLassoMode = () => {
    setLassoMode(p => !p);
    setDrawMode(null);
    setShowDrawPanel(false);
    setSelectedStrokeIndices(new Set());
    setSelectedItemIds(new Set());
    setSelectedStrokeIdx(-1);
    setSelectedId(null);
    setPendingAsset(null);
    setPendingRect(null);
    setPendingLabel(null);
    setShowAssetPanel(false);
    setShowRectPanel(false);
    setShowLabelPanel(false);
  };

  // 키보드
  useEffect(() => {
    const handler = (e) => {
      if (isViewOnly || e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      // Ctrl+Z: 되돌리기
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (drawModeRef.current) {
          // 그리기 모드에서는 마지막 획만 빠르게 삭제
          setStrokes(prev => prev.slice(0, -1));
          setSelectedStrokeIndices(new Set());
        } else {
          undo();
        }
        return;
      }

      // 올가미 모드: Delete/Escape/Ctrl+A/방향키 처리
      if (lassoModeRef.current) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          const selStrokes = selectedStrokeIndicesRef.current;
          const selItems = selectedItemIdsRef.current;
          if (selStrokes.size > 0 || selItems.size > 0) {
            pushHistory(itemsRef.current, strokesRef.current);
          }
          if (selStrokes.size > 0) {
            setStrokes(prev => prev.filter((_, i) => !selStrokes.has(i)));
            setSelectedStrokeIndices(new Set());
          }
          if (selItems.size > 0) {
            setItems(prev => prev.filter(it => !selItems.has(it._id)));
            setSelectedItemIds(new Set());
            setSelectedId(null);
          }
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setSelectedStrokeIndices(new Set());
          setSelectedItemIds(new Set());
          setLassoMode(false);
          return;
        }
        if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
          e.preventDefault();
          setSelectedStrokeIndices(new Set((strokesRef.current || []).map((_, i) => i)));
          setSelectedItemIds(new Set((itemsRef.current || []).map(it => it._id)));
          return;
        }
        // 방향키: 다중 선택 아이템 이동
        if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
          const selItems = selectedItemIdsRef.current;
          if (selItems.size > 0) {
            e.preventDefault();
            const step = e.shiftKey ? 0.00002 : 0.000002;
            const dLat = e.key === 'ArrowUp' ? step : e.key === 'ArrowDown' ? -step : 0;
            const dLng = e.key === 'ArrowRight' ? step : e.key === 'ArrowLeft' ? -step : 0;
            pushHistory(itemsRef.current);
            setItems(prev => prev.map(it => selItems.has(it._id)
              ? { ...it, lat: it.lat + dLat, lng: it.lng + dLng } : it));
          }
          return;
        }
        return;
      }

      // 스트로크 선택 상태에서 키보드 처리
      const selStrokeIdx = selectedStrokeIdxRef.current;
      if (selStrokeIdx >= 0 && strokesRef.current[selStrokeIdx]) {
        if (e.key === 'Delete' || e.key === 'Backspace') {
          e.preventDefault();
          pushHistory(itemsRef.current, strokesRef.current);
          setStrokes(prev => prev.filter((_, i) => i !== selStrokeIdx));
          setSelectedStrokeIdx(-1); setSelectedStrokeIndices(new Set());
        }
        if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v')) {
          e.preventDefault();
          pushHistory(itemsRef.current, strokesRef.current);
          const dup = JSON.parse(JSON.stringify(strokesRef.current[selStrokeIdx]));
          dup.points = dup.points.map(p => ({ lat: p.lat + 0.00003, lng: p.lng + 0.00003 }));
          const newIdx = strokesRef.current.length;
          setStrokes(prev => [...prev, dup]);
          setSelectedStrokeIdx(newIdx); setSelectedStrokeIndices(new Set([newIdx]));
        }
        if (e.key === 'r' || e.key === 'R') {
          pushHistory(itemsRef.current, strokesRef.current);
          const pts = strokesRef.current[selStrokeIdx].points;
          if (pts && pts.length > 0) {
            const cLat = pts.reduce((s, p) => s + p.lat, 0) / pts.length;
            const cLng = pts.reduce((s, p) => s + p.lng, 0) / pts.length;
            const deg = e.shiftKey ? -15 : 15;
            const rad = (deg * Math.PI) / 180;
            const cos = Math.cos(rad), sin = Math.sin(rad);
            setStrokes(prev => prev.map((s, i) => i !== selStrokeIdx ? s : {
              ...s, points: pts.map(p => {
                const dLat = p.lat - cLat, dLng = p.lng - cLng;
                return { lat: cLat + dLat * cos - dLng * sin, lng: cLng + dLat * sin + dLng * cos };
              }),
            }));
          }
        }
        if (e.key === 'Escape') { setSelectedStrokeIdx(-1); setSelectedStrokeIndices(new Set()); setShowDrawPanel(false); }
        return;
      }

      const sel = itemsRef.current.find(i => i._id === selectedIdRef.current);
      const multiSel = selectedItemIdsRef.current;
      if (!sel && multiSel.size === 0) {
        if (e.key === 'Escape') { setPendingAsset(null); setDrawMode(null); setShowDrawPanel(false); setLassoMode(false); }
        return;
      }

      // 방향키 미세 이동 — setPosition 직접 호출 (오버레이 재생성 없음 → sub-pixel 정밀 이동)
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const curPpm = getPixelsPerMeter();
        // 1 native pixel = 항상 눈에 보이는 최소 이동 (Shift=5px)
        const stepM = (e.shiftKey ? 5 : 1) / curPpm;
        const refLat = sel ? sel.lat : 37.5;
        const stepLatDeg = stepM / 111320;
        const stepLngDeg = stepM / (111320 * Math.cos(refLat * Math.PI / 180));
        const dLat = e.key === 'ArrowUp' ? stepLatDeg : e.key === 'ArrowDown' ? -stepLatDeg : 0;
        const dLng = e.key === 'ArrowRight' ? stepLngDeg : e.key === 'ArrowLeft' ? -stepLngDeg : 0;

        // 히스토리는 키 시퀀스 시작 시 1회만
        if (!arrowKeyHistoryPushedRef.current) {
          pushHistory(itemsRef.current);
          arrowKeyHistoryPushedRef.current = true;
        }

        // itemsRef 직접 업데이트 + setPosition 직접 호출 (오버레이 재생성 없음)
        const targetIds = sel ? [selectedIdRef.current] : [...multiSel];
        itemsRef.current = itemsRef.current.map(it => {
          if (!targetIds.includes(it._id)) return it;
          const newLat = it.lat + dLat, newLng = it.lng + dLng;
          const ov = overlayObjsRef.current[it._id];
          if (ov && window.kakao) ov.setPosition(new window.kakao.maps.LatLng(newLat, newLng));
          return { ...it, lat: newLat, lng: newLng };
        });
        // 선택 점선 즉시 갱신 (300ms debounce 기다리지 않음)
        renderDrawingsRef.current?.();

        // 300ms debounce → setItems로 React 상태 sync (저장/undo용)
        if (arrowKeyDebounceRef.current) clearTimeout(arrowKeyDebounceRef.current);
        arrowKeyDebounceRef.current = setTimeout(() => {
          setItems([...itemsRef.current]);
          arrowKeyHistoryPushedRef.current = false;
          arrowKeyDebounceRef.current = null;
        }, 300);
        return;
      }

      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        pushHistory(itemsRef.current);
        setItems(prev => prev.filter(it => it._id !== selectedIdRef.current));
        setSelectedId(null);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'v')) {
        e.preventDefault();
        pushHistory(itemsRef.current);
        const n = { ...sel, _id: `item_${Date.now()}_${itemIdCounter.current++}`, lat: sel.lat + 0.00003, lng: sel.lng + 0.00003 };
        setItems(prev => [...prev, n]); setSelectedId(n._id);
      }
      if (e.key === 'Escape') { setSelectedId(null); setPendingAsset(null); setDrawMode(null); setShowDrawPanel(false); setLassoMode(false); }
      if (e.key === 'r' || e.key === 'R') {
        pushHistory(itemsRef.current);
        setItems(prev => prev.map(it => it._id === selectedIdRef.current
          ? { ...it, rotation: ((it.rotation || 0) + (e.shiftKey ? -15 : 15)) % 360 } : it));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isViewOnly, undo, pushHistory]);

  // Ctrl/Meta 키 상태 트래킹 (Ctrl+드래그 다중선택)
  useEffect(() => {
    const onDown = (e) => { if (e.key === 'Control' || e.key === 'Meta') { ctrlPressedRef.current = true; setCtrlPressed(true); } };
    const onUp = (e) => { if (e.key === 'Control' || e.key === 'Meta') { ctrlPressedRef.current = false; setCtrlPressed(false); } };
    // 창 전환 시 Ctrl 눌린 상태 고착 방지
    const onBlur = () => { ctrlPressedRef.current = false; setCtrlPressed(false); };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Ctrl 눌린 동안 지도 드래그 비활성 (드래그 선택 가능하도록)
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || drawMode || lassoMode) return;
    map.setDraggable(!ctrlPressed);
    map.setZoomable(!ctrlPressed);
  }, [ctrlPressed, drawMode, lassoMode, mapReady]);

  // Ctrl+드래그 선택 박스 (일반 모드, 빈 공간)
  useEffect(() => {
    const canvas = drawCanvasRef.current;
    if (!canvas || drawMode || lassoMode || !mapReady || !ctrlPressed) {
      if (canvas && !drawMode && !lassoMode) canvas.style.cursor = 'default';
      return;
    }
    canvas.style.pointerEvents = 'auto';
    canvas.style.cursor = 'crosshair';
    const container = canvas.parentElement;
    if (container) { canvas.width = container.clientWidth; canvas.height = container.clientHeight; }

    let isDragging = false;
    let selectRect = null;

    const getXY = (e) => {
      const r = canvas.getBoundingClientRect();
      return { mx: e.clientX - r.left, my: e.clientY - r.top };
    };
    const onDown = (e) => {
      if (!ctrlPressedRef.current) return;
      e.preventDefault(); e.stopPropagation();
      isDragging = true;
      const { mx, my } = getXY(e);
      selectRect = { startX: mx, startY: my, endX: mx, endY: my };
    };
    const onMove = (e) => {
      if (!isDragging || !selectRect) return;
      const { mx, my } = getXY(e);
      selectRect.endX = mx; selectRect.endY = my;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const x = Math.min(selectRect.startX, selectRect.endX);
      const y = Math.min(selectRect.startY, selectRect.endY);
      const w = Math.abs(selectRect.endX - selectRect.startX);
      const h = Math.abs(selectRect.endY - selectRect.startY);
      ctx.strokeStyle = '#a78bfa'; ctx.lineWidth = 1.5;
      ctx.setLineDash([5, 3]); ctx.strokeRect(x, y, w, h);
      ctx.fillStyle = 'rgba(167,139,250,0.08)'; ctx.fillRect(x, y, w, h);
      ctx.setLineDash([]);
    };
    const onUp = () => {
      if (!isDragging || !selectRect) return;
      isDragging = false;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const x1 = Math.min(selectRect.startX, selectRect.endX);
      const x2 = Math.max(selectRect.startX, selectRect.endX);
      const y1 = Math.min(selectRect.startY, selectRect.endY);
      const y2 = Math.max(selectRect.startY, selectRect.endY);
      if (Math.abs(x2 - x1) > 5 || Math.abs(y2 - y1) > 5) {
        const map = mapInstanceRef.current;
        if (map && window.kakao) {
          const proj = map.getProjection();
          const ez = extraZoomRef.current;
          const cw = canvas.width / 2, ch = canvas.height / 2;
          const ids = new Set();
          itemsRef.current.forEach(it => {
            const p = proj.containerPointFromCoords(new window.kakao.maps.LatLng(it.lat, it.lng));
            const px = cw + (p.x - cw) * ez, py = ch + (p.y - ch) * ez;
            if (px >= x1 && px <= x2 && py >= y1 && py <= y2) ids.add(it._id);
          });
          if (ids.size > 0) {
            setSelectedItemIds(new Set([...selectedItemIdsRef.current, ...ids]));
            setSelectedId(null);
            setLassoMode(true); // 선택 후 올가미 모드로 자동 전환 (이동/삭제 가능)
          }
        }
      }
      selectRect = null;
    };
    canvas.addEventListener('mousedown', onDown);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      canvas.removeEventListener('mousedown', onDown);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // lassoMode/drawMode가 아닐 때만 초기화 (lasso 전환 시 덮어쓰면 안됨)
      // ref는 이미 업데이트된 상태이므로 현재 상태를 정확히 반영
      if (!lassoModeRef.current && !drawModeRef.current && !(strokesRef.current?.length)) {
        canvas.style.pointerEvents = 'none';
        canvas.style.cursor = 'default';
      }
      const ctx = canvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    };
  }, [ctrlPressed, drawMode, lassoMode, mapReady, setSelectedItemIds, setSelectedId, setLassoMode]);

  // 정렬 함수 (다중 선택 시) — Kakao 프로젝션 화면 좌표 기반 (픽셀 정밀도)
  const alignItems = useCallback((direction) => {
    const selIds = selectedItemIds;
    if (selIds.size < 2) return;
    const selItems = items.filter(it => selIds.has(it._id));
    const map = mapInstanceRef.current;
    if (!map || !window.kakao) return;
    pushHistory(items);
    const proj = map.getProjection();
    const kakao = window.kakao;
    const ppm = getPixelsPerMeter();
    const newPositions = {};

    // 컨테이너 좌표로 아이템 중심/엣지 계산 (extraZoom 무관, 프로젝션 기준)
    const getEdges = (it) => {
      const p = proj.containerPointFromCoords(new kakao.maps.LatLng(it.lat, it.lng));
      const hw = (it.widthMeter || 1) * ppm / 2;
      const hh = (it.heightMeter || 1) * ppm / 2;
      return { left: p.x - hw, right: p.x + hw, top: p.y - hh, bottom: p.y + hh, cx: p.x, cy: p.y };
    };
    // 컨테이너 좌표 → lat/lng
    const toLngLat = (cx, cy) => {
      const ll = proj.coordsFromContainerPoint(new kakao.maps.Point(cx, cy));
      return { lat: ll.getLat(), lng: ll.getLng() };
    };

    if (direction === 'left') {
      const minLeft = Math.min(...selItems.map(it => getEdges(it).left));
      selItems.forEach(it => {
        const { cy } = getEdges(it);
        newPositions[it._id] = toLngLat(minLeft + (it.widthMeter || 1) * ppm / 2, cy);
      });
    } else if (direction === 'right') {
      const maxRight = Math.max(...selItems.map(it => getEdges(it).right));
      selItems.forEach(it => {
        const { cy } = getEdges(it);
        newPositions[it._id] = toLngLat(maxRight - (it.widthMeter || 1) * ppm / 2, cy);
      });
    } else if (direction === 'hcenter') {
      const allLeft = Math.min(...selItems.map(it => getEdges(it).left));
      const allRight = Math.max(...selItems.map(it => getEdges(it).right));
      const centerX = (allLeft + allRight) / 2;
      selItems.forEach(it => {
        const { cy } = getEdges(it);
        newPositions[it._id] = toLngLat(centerX, cy);
      });
    } else if (direction === 'top') {
      // 화면 y 좌표: 위 = 작은 값 (북쪽)
      const minTop = Math.min(...selItems.map(it => getEdges(it).top));
      selItems.forEach(it => {
        const { cx } = getEdges(it);
        newPositions[it._id] = toLngLat(cx, minTop + (it.heightMeter || 1) * ppm / 2);
      });
    } else if (direction === 'bottom') {
      const maxBottom = Math.max(...selItems.map(it => getEdges(it).bottom));
      selItems.forEach(it => {
        const { cx } = getEdges(it);
        newPositions[it._id] = toLngLat(cx, maxBottom - (it.heightMeter || 1) * ppm / 2);
      });
    } else if (direction === 'vcenter') {
      const allTop = Math.min(...selItems.map(it => getEdges(it).top));
      const allBottom = Math.max(...selItems.map(it => getEdges(it).bottom));
      const centerY = (allTop + allBottom) / 2;
      selItems.forEach(it => {
        const { cx } = getEdges(it);
        newPositions[it._id] = toLngLat(cx, centerY);
      });
    } else if (direction === 'hdist') {
      const sorted = selItems.map(it => ({ it, e: getEdges(it) })).sort((a, b) => a.e.left - b.e.left);
      const totalW = sorted.reduce((s, { it }) => s + (it.widthMeter || 1) * ppm, 0);
      const conL = sorted[0].e.left;
      const conR = sorted[sorted.length - 1].e.right;
      const gap = (conR - conL - totalW) / (sorted.length - 1);
      let curLeft = conL;
      sorted.forEach(({ it, e }) => {
        const hw = (it.widthMeter || 1) * ppm / 2;
        newPositions[it._id] = toLngLat(curLeft + hw, e.cy);
        curLeft += (it.widthMeter || 1) * ppm + gap;
      });
    } else if (direction === 'vdist') {
      const sorted = selItems.map(it => ({ it, e: getEdges(it) })).sort((a, b) => a.e.top - b.e.top);
      const totalH = sorted.reduce((s, { it }) => s + (it.heightMeter || 1) * ppm, 0);
      const conT = sorted[0].e.top;
      const conB = sorted[sorted.length - 1].e.bottom;
      const gap = (conB - conT - totalH) / (sorted.length - 1);
      let curTop = conT;
      sorted.forEach(({ it, e }) => {
        const hh = (it.heightMeter || 1) * ppm / 2;
        newPositions[it._id] = toLngLat(e.cx, curTop + hh);
        curTop += (it.heightMeter || 1) * ppm + gap;
      });
    }
    setItems(prev => prev.map(it => {
      const p = newPositions[it._id];
      return p ? { ...it, ...p } : it;
    }));
  }, [selectedItemIds, items, pushHistory, getPixelsPerMeter]);

  // 컨텍스트 메뉴: Escape 키로 닫기
  useEffect(() => {
    if (!contextMenu.visible) return;
    const handler = (e) => { if (e.key === 'Escape') setContextMenu({ visible: false, x: 0, y: 0, itemId: null }); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [contextMenu.visible]);

  const copyToClipboard = (text, msg) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => alert(msg));
    } else {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      alert(msg);
    }
  };

  const copyShareLink = (editable) => {
    if (!project) return;
    const url = editable
      ? `${window.location.origin}/events/share/${project.shareId}?edit=1`
      : `${window.location.origin}/events/view/${project.shareId}`;
    copyToClipboard(url, editable ? '편집 가능 링크가 복사되었습니다.' : '보기용 링크가 복사되었습니다.');
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-[#0d0b14] text-white/40">로딩 중...</div>;
  if (mapError) return (
    <div className="flex items-center justify-center h-screen bg-[#0d0b14] text-center text-white/60">
      <div><p className="text-lg mb-2">지도를 불러올 수 없습니다</p><p className="text-sm text-white/40">{mapError}</p></div>
    </div>
  );

  return (
    <div className="h-screen flex flex-col bg-[#0d0b14]">
      {/* 상단바: PC 1줄 (제목 | 도구(중앙) | 공유/저장), 태블릿/모바일 2줄 */}
      <div className="bg-[#1a1428] border-b border-white/10 z-[1000]">
        {/* PC: 1줄 레이아웃 */}
        <div className="hidden lg:flex items-center px-4 py-1.5">
          {/* 좌: 뒤로가기 + 파비콘 + 제목 */}
          <div className="flex items-center gap-2 min-w-0 w-56 flex-shrink-0">
            {!shareId && <Link to="/events" className="text-white/40 hover:text-white flex-shrink-0 mr-1"><ArrowLeft size={18} /></Link>}
            <div className="w-7 h-7 flex-shrink-0 bg-white/20 rounded-lg flex items-center justify-center"><img src="/favicon.svg" alt="" className="w-5 h-5" /></div>
            {shareId ? (
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white font-medium text-sm truncate">{project?.title || '배치도'}</span>
                {isSharedEdit && (
                  shareEditable
                    ? <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">편집가능</span>
                    : <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/40">보기전용</span>
                )}
              </div>
            ) : (
              <button onClick={openEditProject} className="flex items-center gap-1.5 text-white font-medium text-sm truncate hover:text-violet-400 transition-colors" title="제목/설명 수정">
                <span className="truncate">{project?.title || '배치도'}</span>
                <Pencil size={11} className="text-white/30 flex-shrink-0" />
              </button>
            )}
          </div>
          {/* 중앙: 도구 버튼 */}
          {!isViewOnly && (
            <div className="flex-1 flex items-center justify-center gap-3">
              {/* 뷰 도구 */}
              <div className="flex items-center gap-1 bg-white/[0.03] rounded-lg px-1 py-0.5">
                <div className="flex items-center bg-white/5 rounded overflow-hidden">
                  <button onClick={() => setMapType('ROADMAP')}
                    className={`p-2 transition-colors ${mapType === 'ROADMAP' ? 'bg-violet-500 text-white' : 'text-white/60 hover:text-white'}`}
                    title="일반지도"><Map size={18} /></button>
                  <button onClick={() => setMapType('HYBRID')}
                    className={`p-2 transition-colors ${mapType !== 'ROADMAP' ? 'bg-violet-500 text-white' : 'text-white/60 hover:text-white'}`}
                    title="위성"><Satellite size={18} /></button>
                </div>
                {mapType !== 'ROADMAP' && (
                  <button onClick={() => setMapLabels(v => !v)}
                    className={`p-2 rounded transition-colors ${mapLabels ? 'bg-white/10 text-white' : 'bg-white/5 text-white/30'}`}
                    title={mapLabels ? '지도 라벨 끄기' : '지도 라벨 켜기'}>
                    {mapLabels ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                )}
                <button onClick={() => setShowRoadview(v => !v)}
                  className={`p-2 rounded transition-colors ${showRoadview ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="로드뷰">
                  <ScanEye size={18} />
                </button>
              </div>
              {/* 오브젝트 도구 */}
              <div className="flex items-center gap-1 bg-white/[0.03] rounded-lg px-1 py-0.5">
                <button onClick={() => { setShowAssetPanel(!showAssetPanel); setPendingAsset(null); setShowRectPanel(false); setPendingRect(null); setShowLabelPanel(false); setPendingLabel(null); setDrawMode(null); setShowDrawPanel(false); setLassoMode(false); }}
                  className={`p-2 rounded transition-colors ${showAssetPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="에셋 패널">
                  <Package size={18} />
                </button>
                <button onClick={toggleRectPanel}
                  className={`p-2 rounded transition-colors ${showRectPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="영역 추가">
                  <Square size={18} />
                </button>
                <button onClick={toggleLabelPanel}
                  className={`p-2 rounded transition-colors ${showLabelPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="라벨 추가">
                  <Type size={18} />
                </button>
                <button onClick={toggleDrawPanel}
                  className={`p-2 rounded transition-colors ${showDrawPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="그리기">
                  <Paintbrush size={18} />
                </button>
                <button onClick={toggleLassoMode}
                  className={`p-2 rounded transition-colors ${lassoMode ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="올가미 (그리기 선택/이동/삭제)">
                  <Lasso size={18} />
                </button>
                <div className="w-px h-5 bg-white/10" />
                <button onClick={undo} disabled={historyRef.current.length === 0}
                  className="p-2 bg-white/5 text-white/60 hover:text-white disabled:opacity-30 rounded" title="되돌리기 (Ctrl+Z)">
                  <Undo2 size={18} />
                </button>
                {hasSelection && (<>
                  <div className="w-px h-5 bg-white/10" />
                  <button onClick={() => rotateSelected(15)} className="p-2 bg-white/5 text-white/60 hover:text-white rounded" title="회전 (R)"><RotateCw size={18} /></button>
                  <button onClick={duplicateSelected} className="p-2 bg-white/5 text-white/60 hover:text-white rounded" title="복제"><Copy size={18} /></button>
                  <button onClick={deleteSelected} className="p-2 bg-white/5 text-red-400/60 hover:text-red-400 rounded" title="삭제 (Del)"><Trash2 size={18} /></button>
                </>)}
                {lassoMode && (selectedStrokeIndices.size + selectedItemIds.size) > 0 && (<>
                  <div className="w-px h-5 bg-white/10" />
                  <span className="text-[11px] text-violet-300">{selectedStrokeIndices.size + selectedItemIds.size}개 선택</span>
                  <button onClick={() => {
                    pushHistory(itemsRef.current, strokesRef.current);
                    if (selectedStrokeIndices.size > 0) {
                      setStrokes(prev => prev.filter((_, i) => !selectedStrokeIndices.has(i)));
                      setSelectedStrokeIndices(new Set());
                    }
                    if (selectedItemIds.size > 0) {
                      setItems(prev => prev.filter(it => !selectedItemIds.has(it._id)));
                      setSelectedItemIds(new Set());
                      setSelectedId(null);
                    }
                  }} className="p-2 bg-white/5 text-red-400/60 hover:text-red-400 rounded" title="선택 삭제 (Del)"><Trash2 size={18} /></button>
                  {/* 정렬 버튼 (아이템 2개 이상 선택 시) */}
                  {selectedItemIds.size >= 2 && (<>
                    <div className="w-px h-5 bg-white/10" />
                    <div className="flex items-center gap-0.5 bg-white/[0.03] rounded-lg px-1 py-0.5">
                      <button onClick={() => alignItems('left')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="왼쪽 정렬"><AlignStartVertical size={14} /></button>
                      <button onClick={() => alignItems('hcenter')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="가로 중앙 정렬"><AlignCenterVertical size={14} /></button>
                      <button onClick={() => alignItems('right')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="오른쪽 정렬"><AlignEndVertical size={14} /></button>
                      <div className="w-px h-3 bg-white/10" />
                      <button onClick={() => alignItems('top')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="위쪽 정렬"><AlignLeft size={14} className="rotate-90" /></button>
                      <button onClick={() => alignItems('vcenter')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="세로 중앙 정렬"><AlignCenter size={14} className="rotate-90" /></button>
                      <button onClick={() => alignItems('bottom')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="아래쪽 정렬"><AlignRight size={14} className="rotate-90" /></button>
                      <div className="w-px h-3 bg-white/10" />
                      <button onClick={() => alignItems('hdist')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="가로 간격 균등"><AlignHorizontalSpaceBetween size={14} /></button>
                      <button onClick={() => alignItems('vdist')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="세로 간격 균등"><AlignVerticalSpaceBetween size={14} /></button>
                    </div>
                  </>)}
                </>)}
              </div>
              {assets.filter(a => favAssetIds.includes(a.id)).length > 0 && (<>
                <div className="w-px h-5 bg-white/10" />
                {assets.filter(a => favAssetIds.includes(a.id)).map(a => (
                  <button key={a.id} onClick={() => setPendingAsset(pendingAsset?.id === a.id ? null : a)}
                    className={`w-8 h-8 rounded flex-shrink-0 flex items-center justify-center overflow-hidden transition-all ${pendingAsset?.id === a.id ? 'ring-2 ring-violet-400 scale-110' : 'hover:scale-105 opacity-80 hover:opacity-100'}`}
                    style={{ backgroundColor: a.imageUrl ? '#ffffff10' : (a.color || '#6366f1') + '60' }}
                    title={`${a.name} (${a.widthMeter}m×${a.heightMeter}m)`}>
                    {a.imageUrl
                      ? <img src={a.imageUrl} alt={a.name} className="w-full h-full object-contain" />
                      : <span className="text-white text-[8px] font-bold">{a.name.slice(0, 2)}</span>}
                  </button>
                ))}
              </>)}
            </div>
          )}
          {/* 우: 공유/저장/로고 */}
          <div className="flex items-center gap-2 flex-shrink-0 w-56 justify-end">
            {!isViewOnly && (<>
              <button onClick={() => copyShareLink(false)} className="p-1.5 bg-white/5 text-white/60 hover:text-white rounded-lg" title="보기용 링크"><Link2 size={16} /></button>
              {!shareId && (<>
                <button onClick={() => copyShareLink(true)} className={`p-1.5 bg-white/5 rounded-lg transition-colors ${shareEditable ? 'text-violet-400' : 'text-white/30'}`} title={shareEditable ? '편집 가능 링크 복사' : '편집 공유가 꺼져있습니다'}>
                  <Pencil size={14} />
                </button>
                <button onClick={async () => {
                  const next = !shareEditable;
                  try {
                    await api.put(`/event-layout/projects/${project.id}`, { ...project, shareEditable: next });
                    setShareEditable(next);
                    setProject(prev => ({ ...prev, shareEditable: next }));
                    alert(next ? '편집 공유가 활성화되었습니다.' : '편집 공유가 비활성화되었습니다.');
                  } catch { alert('변경 중 오류가 발생했습니다.'); }
                }} className={`px-1.5 py-1 text-[10px] rounded transition-colors ${shareEditable ? 'bg-violet-500/30 text-violet-300' : 'bg-white/5 text-white/30'}`}
                  title={shareEditable ? '편집 공유 끄기' : '편집 공유 켜기'}>
                  {shareEditable ? 'ON' : 'OFF'}
                </button>
              </>)}
              <button onClick={() => handleSave()} disabled={saving}
                className="flex items-center gap-1 px-3 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-sm rounded-lg">
                <Save size={14} /> {saving ? '...' : '저장'}
              </button>
              <button onClick={toggleHistory}
                className={`p-1.5 rounded-lg transition-colors ${showHistory ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`}
                title="히스토리">
                <Clock size={16} />
              </button>
            </>)}
          </div>
        </div>

        {/* 모바일/태블릿: 2줄 레이아웃 */}
        <div className="lg:hidden">
          {/* 1줄: 파비콘 + 제목 + 공유/저장 */}
          <div className="flex items-center justify-between px-3 py-1.5">
            <div className="flex items-center gap-1.5 min-w-0">
              {!shareId && <Link to="/events" className="text-white/40 hover:text-white flex-shrink-0 mr-0.5"><ArrowLeft size={18} /></Link>}
              <div className="w-7 h-7 flex-shrink-0 bg-white/20 rounded-lg flex items-center justify-center"><img src="/favicon.svg" alt="" className="w-5 h-5" /></div>
              {shareId ? (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-white font-medium text-sm truncate">{project?.title || '배치도'}</span>
                  {isSharedEdit && (
                    shareEditable
                      ? <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400">편집가능</span>
                      : <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/40">보기전용</span>
                  )}
                </div>
              ) : (
                <button onClick={openEditProject} className="flex items-center gap-1.5 text-white font-medium text-sm truncate hover:text-violet-400 transition-colors" title="제목/설명 수정">
                  <span className="truncate">{project?.title || '배치도'}</span>
                  <Pencil size={11} className="text-white/30 flex-shrink-0" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {!isViewOnly && (<>
                <button onClick={() => copyShareLink(false)} className="p-1.5 bg-white/5 text-white/60 hover:text-white rounded-lg" title="보기용 링크"><Link2 size={16} /></button>
                {!shareId && (<>
                  <button onClick={() => copyShareLink(true)} className={`p-1.5 bg-white/5 rounded-lg transition-colors ${shareEditable ? 'text-violet-400' : 'text-white/30'}`} title={shareEditable ? '편집 가능 링크 복사' : '편집 공유가 꺼져있습니다'}>
                    <Pencil size={14} />
                  </button>
                  <button onClick={async () => {
                    const next = !shareEditable;
                    try {
                      await api.put(`/event-layout/projects/${project.id}`, { ...project, shareEditable: next });
                      setShareEditable(next);
                      setProject(prev => ({ ...prev, shareEditable: next }));
                      alert(next ? '편집 공유가 활성화되었습니다.' : '편집 공유가 비활성화되었습니다.');
                    } catch { alert('변경 중 오류가 발생했습니다.'); }
                  }} className={`px-1.5 py-1 text-[10px] rounded transition-colors ${shareEditable ? 'bg-violet-500/30 text-violet-300' : 'bg-white/5 text-white/30'}`}
                    title={shareEditable ? '편집 공유 끄기' : '편집 공유 켜기'}>
                    {shareEditable ? 'ON' : 'OFF'}
                  </button>
                </>)}
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1 px-3 py-1.5 bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-white text-xs rounded-lg">
                  <Save size={14} /> {saving ? '...' : '저장'}
                </button>
                <button onClick={toggleHistory}
                  className={`p-1.5 rounded-lg transition-colors ${showHistory ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`}
                  title="히스토리">
                  <Clock size={14} />
                </button>
              </>)}
            </div>
          </div>
          {/* 2줄: 도구 (편집 모드만) */}
          {!isViewOnly && (
            <div className="flex items-center gap-2 px-3 py-1 border-t border-white/5 overflow-x-auto">
              {/* 뷰 도구 */}
              <div className="flex items-center gap-1 bg-white/[0.03] rounded-lg px-1 py-0.5 flex-shrink-0">
                <div className="flex items-center bg-white/5 rounded overflow-hidden">
                  <button onClick={() => setMapType('ROADMAP')}
                    className={`p-2 transition-colors ${mapType === 'ROADMAP' ? 'bg-violet-500 text-white' : 'text-white/60 hover:text-white'}`}
                    title="일반지도"><Map size={18} /></button>
                  <button onClick={() => setMapType('HYBRID')}
                    className={`p-2 transition-colors ${mapType !== 'ROADMAP' ? 'bg-violet-500 text-white' : 'text-white/60 hover:text-white'}`}
                    title="위성"><Satellite size={18} /></button>
                </div>
                {mapType !== 'ROADMAP' && (
                  <button onClick={() => setMapLabels(v => !v)}
                    className={`p-2 rounded transition-colors ${mapLabels ? 'bg-white/10 text-white' : 'bg-white/5 text-white/30'}`}
                    title={mapLabels ? '지도 라벨 끄기' : '지도 라벨 켜기'}>
                    {mapLabels ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                )}
                <button onClick={() => setShowRoadview(v => !v)}
                  className={`p-2 rounded transition-colors ${showRoadview ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="로드뷰">
                  <ScanEye size={18} />
                </button>
              </div>
              {/* 오브젝트 도구 */}
              <div className="flex items-center gap-1 bg-white/[0.03] rounded-lg px-1 py-0.5 flex-shrink-0">
                <button onClick={() => { setShowAssetPanel(!showAssetPanel); setPendingAsset(null); setShowRectPanel(false); setPendingRect(null); setShowLabelPanel(false); setPendingLabel(null); setDrawMode(null); setShowDrawPanel(false); setLassoMode(false); }}
                  className={`p-2 rounded transition-colors ${showAssetPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="에셋 패널">
                  <Package size={18} />
                </button>
                <button onClick={toggleRectPanel}
                  className={`p-2 rounded transition-colors ${showRectPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="영역 추가">
                  <Square size={18} />
                </button>
                <button onClick={toggleLabelPanel}
                  className={`p-2 rounded transition-colors ${showLabelPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="라벨 추가">
                  <Type size={18} />
                </button>
                <button onClick={toggleDrawPanel}
                  className={`p-2 rounded transition-colors ${showDrawPanel ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="그리기">
                  <Paintbrush size={18} />
                </button>
                <button onClick={toggleLassoMode}
                  className={`p-2 rounded transition-colors ${lassoMode ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`} title="올가미">
                  <Lasso size={18} />
                </button>
                <div className="w-px h-5 bg-white/10" />
                <button onClick={undo} disabled={historyRef.current.length === 0}
                  className="p-2 bg-white/5 text-white/60 hover:text-white disabled:opacity-30 rounded" title="되돌리기 (Ctrl+Z)">
                  <Undo2 size={18} />
                </button>
                {hasSelection && (<>
                  <div className="w-px h-5 bg-white/10" />
                  <button onClick={() => rotateSelected(15)} className="p-2 bg-white/5 text-white/60 hover:text-white rounded" title="회전 (R)"><RotateCw size={18} /></button>
                  <button onClick={duplicateSelected} className="p-2 bg-white/5 text-white/60 hover:text-white rounded" title="복제"><Copy size={18} /></button>
                  <button onClick={deleteSelected} className="p-2 bg-white/5 text-red-400/60 hover:text-red-400 rounded" title="삭제 (Del)"><Trash2 size={18} /></button>
                </>)}
                {lassoMode && (selectedStrokeIndices.size + selectedItemIds.size) > 0 && (<>
                  <div className="w-px h-5 bg-white/10" />
                  <span className="text-[11px] text-violet-300">{selectedStrokeIndices.size + selectedItemIds.size}개</span>
                  <button onClick={() => {
                    pushHistory(itemsRef.current, strokesRef.current);
                    if (selectedStrokeIndices.size > 0) {
                      setStrokes(prev => prev.filter((_, i) => !selectedStrokeIndices.has(i)));
                      setSelectedStrokeIndices(new Set());
                    }
                    if (selectedItemIds.size > 0) {
                      setItems(prev => prev.filter(it => !selectedItemIds.has(it._id)));
                      setSelectedItemIds(new Set());
                      setSelectedId(null);
                    }
                  }} className="p-2 bg-white/5 text-red-400/60 hover:text-red-400 rounded" title="선택 삭제"><Trash2 size={18} /></button>
                  {selectedItemIds.size >= 2 && (<>
                    <div className="w-px h-5 bg-white/10" />
                    <button onClick={() => alignItems('left')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="왼쪽 정렬"><AlignStartVertical size={14} /></button>
                    <button onClick={() => alignItems('hcenter')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="가로 중앙"><AlignCenterVertical size={14} /></button>
                    <button onClick={() => alignItems('right')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="오른쪽 정렬"><AlignEndVertical size={14} /></button>
                    <button onClick={() => alignItems('top')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="위쪽 정렬"><AlignLeft size={14} className="rotate-90" /></button>
                    <button onClick={() => alignItems('vcenter')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="세로 중앙"><AlignCenter size={14} className="rotate-90" /></button>
                    <button onClick={() => alignItems('bottom')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="아래쪽 정렬"><AlignRight size={14} className="rotate-90" /></button>
                    <button onClick={() => alignItems('hdist')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="가로 간격 균등"><AlignHorizontalSpaceBetween size={14} /></button>
                    <button onClick={() => alignItems('vdist')} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 rounded" title="세로 간격 균등"><AlignVerticalSpaceBetween size={14} /></button>
                  </>)}
                </>)}
              </div>
              {assets.filter(a => favAssetIds.includes(a.id)).length > 0 && (<>
                <div className="w-px h-5 bg-white/10 flex-shrink-0" />
                {assets.filter(a => favAssetIds.includes(a.id)).map(a => (
                  <button key={a.id} onClick={() => setPendingAsset(pendingAsset?.id === a.id ? null : a)}
                    className={`w-8 h-8 rounded flex-shrink-0 flex items-center justify-center overflow-hidden transition-all ${pendingAsset?.id === a.id ? 'ring-2 ring-violet-400 scale-110' : 'hover:scale-105 opacity-80 hover:opacity-100'}`}
                    style={{ backgroundColor: a.imageUrl ? '#ffffff10' : (a.color || '#6366f1') + '60' }}
                    title={`${a.name} (${a.widthMeter}m×${a.heightMeter}m)`}>
                    {a.imageUrl
                      ? <img src={a.imageUrl} alt={a.name} className="w-full h-full object-contain" />
                      : <span className="text-white text-[8px] font-bold">{a.name.slice(0, 2)}</span>}
                  </button>
                ))}
              </>)}
            </div>
          )}
        </div>
      </div>

      {/* 프로젝트 정보 수정 모달 */}
      {showEditProject && (
        <div className="fixed inset-0 bg-black/60 z-[2000] flex items-center justify-center p-4" onClick={() => setShowEditProject(false)}>
          <div className="bg-[#1a1428] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-medium text-white mb-4">배치도 정보 수정</h2>
            <form onSubmit={handleEditProject} className="space-y-4">
              <div>
                <label className="block text-sm text-white/60 mb-1">제목</label>
                <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                  autoFocus />
              </div>
              <div>
                <label className="block text-sm text-white/60 mb-1">설명</label>
                <input type="text" value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                  placeholder="메모" />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setShowEditProject(false)}
                  className="flex-1 py-2.5 text-white/60 text-sm border border-white/10 rounded-lg hover:text-white transition-colors">취소</button>
                <button type="submit"
                  className="flex-1 py-2.5 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg transition-colors">저장</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="flex-1 relative overflow-hidden">
        {/* 에셋 패널 (오버레이) */}
        {showAssetPanel && !isViewOnly && (
          <div className="absolute top-0 left-0 w-64 h-full bg-[#1a1428]/95 backdrop-blur-sm border-r border-white/10 overflow-y-auto z-[500]">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">에셋 목록</h3>
                <div className="flex items-center gap-1.5">
                  {!shareId && <button onClick={() => setShowAiGen(!showAiGen)}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded-md transition-colors ${showAiGen ? 'bg-fuchsia-500/30 text-fuchsia-300' : 'bg-white/5 text-white/40 hover:text-white/60'}`}>
                    <Sparkles size={10} /> AI 생성
                  </button>}
                  <button onClick={() => { setShowAssetPanel(false); setPendingAsset(null); }} className="text-white/30 hover:text-white/60 text-sm leading-none">✕</button>
                </div>
              </div>
              {showAiGen && (
                <div className="mb-3 p-2 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-lg space-y-2">
                  <textarea value={aiDesc} onChange={e => setAiDesc(e.target.value)} rows={2} disabled={aiGenerating}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-[11px] outline-none focus:border-fuchsia-400 resize-none"
                    placeholder="예: 몽골텐트, 빨간색 대형 무대..." />
                  <button onClick={async () => {
                    if (!aiDesc) return;
                    if (aiPreview?.s3Key) {
                      api.post('/event-layout/assets/generate-discard', { s3Key: aiPreview.s3Key }).catch(() => {});
                    }
                    setAiGenerating(true);
                    setAiPreview(null);
                    try {
                      const res = await api.post('/event-layout/assets/generate-preview', { description: aiDesc });
                      setAiPreview(res.data);
                    } catch (err) {
                      alert(err.response?.data?.message || 'AI 생성 실패');
                    } finally {
                      setAiGenerating(false);
                    }
                  }} disabled={aiGenerating || !aiDesc}
                    className="w-full flex items-center justify-center gap-1 py-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 disabled:opacity-50 text-white text-[11px] rounded transition-colors">
                    {aiGenerating ? <><Loader2 size={11} className="animate-spin" /> 생성 중...</> : aiPreview ? <><Sparkles size={11} /> 다시 생성</> : <><Sparkles size={11} /> 생성</>}
                  </button>
                  {aiPreview && (
                    <>
                      <div className="rounded overflow-hidden border border-white/10" style={{ backgroundImage: 'repeating-conic-gradient(#ffffff10 0% 25%, transparent 0% 50%)', backgroundSize: '12px 12px' }}>
                        <img src={aiPreview.imageUrl} alt="" className="w-full aspect-square object-contain" />
                      </div>
                      <button onClick={async () => {
                        try {
                          await api.post('/event-layout/assets/generate-confirm', {
                            imageUrl: aiPreview.imageUrl, s3Key: aiPreview.s3Key,
                            name: aiDesc.slice(0, 30), category: 'general',
                            widthMeter: '2', heightMeter: '2',
                          });
                          setAiPreview(null);
                          setAiDesc('');
                          setShowAiGen(false);
                          const r = await api.get('/event-layout/assets');
                          setAssets(r.data);
                        } catch (err) {
                          alert(err.response?.data?.message || '저장 실패');
                        }
                      }} className="w-full flex items-center justify-center gap-1 py-1.5 bg-emerald-500/80 hover:bg-emerald-500 text-white text-[11px] rounded transition-colors">
                        확정
                      </button>
                    </>
                  )}
                </div>
              )}
              {pendingAsset && (
                <div className="mb-3 p-2 bg-violet-500/20 border border-violet-500/30 rounded-lg">
                  <p className="text-violet-400 text-xs">지도를 클릭하여 배치: <strong>{pendingAsset.name}</strong></p>
                  <button onClick={() => setPendingAsset(null)} className="text-white/40 text-xs mt-1 hover:text-white">취소 (Esc)</button>
                </div>
              )}
              {assets.length === 0 ? (
                <div className="text-white/30 text-sm">
                  <p>에셋이 없습니다.</p>
                  <Link to="/events/assets" className="text-violet-400 text-xs hover:underline mt-1 inline-block">에셋 등록하기</Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {assets.map(a => (
                    <div key={a.id} className={`flex items-center gap-1 rounded-lg ${pendingAsset?.id === a.id ? 'bg-violet-500/20 border border-violet-500/30' : 'bg-white/5 hover:bg-white/10 border border-transparent'}`}>
                      <button onClick={() => setPendingAsset(pendingAsset?.id === a.id ? null : a)}
                        className="flex-1 flex items-center gap-3 p-2 text-left min-w-0">
                        <div className="w-10 h-10 rounded flex-shrink-0 flex items-center justify-center overflow-hidden"
                          style={{ backgroundColor: a.imageUrl ? '#ffffff10' : (a.color || '#6366f1') + '40' }}>
                          {a.imageUrl ? <img src={a.imageUrl} alt="" className="w-full h-full object-contain" /> :
                            <div className="w-7 h-7 rounded" style={{ backgroundColor: a.color || '#6366f1' }} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-medium truncate">{a.name}</p>
                          <p className="text-white/30 text-[10px]">{a.widthMeter}m x {a.heightMeter}m</p>
                        </div>
                      </button>
                      <button onClick={() => toggleFav(a.id)} className={`p-1.5 flex-shrink-0 transition-colors ${favAssetIds.includes(a.id) ? 'text-amber-400' : 'text-white/15 hover:text-white/30'}`} title={favAssetIds.includes(a.id) ? '즐겨찾기 해제' : '즐겨찾기'}>
                        <Star size={12} fill={favAssetIds.includes(a.id) ? 'currentColor' : 'none'} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 영역 설정 패널 (오버레이) */}
        {showRectPanel && !isViewOnly && (
          <div className="absolute top-0 left-0 w-64 h-full bg-[#1a1428]/95 backdrop-blur-sm border-r border-white/10 overflow-y-auto z-[500]">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">영역 설정</h3>
                <button onClick={() => { setShowRectPanel(false); setPendingRect(null); }} className="text-white/30 hover:text-white/60 text-sm leading-none">✕</button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-white/40 text-[11px] mb-1">이름</label>
                  <textarea rows={2} value={rectForm.label} onChange={e => setRectForm({ ...rectForm, label: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400 resize-y" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-white/40 text-[11px] mb-1">가로 (m)</label>
                    <input type="number" value={rectForm.widthMeter} onChange={e => setRectForm({ ...rectForm, widthMeter: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400" min="1" step="0.5" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-white/40 text-[11px] mb-1">세로 (m)</label>
                    <input type="number" value={rectForm.heightMeter} onChange={e => setRectForm({ ...rectForm, heightMeter: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400" min="1" step="0.5" />
                  </div>
                </div>
                <div>
                  <label className="block text-white/40 text-[11px] mb-1">색상</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={rectForm.color} onChange={e => setRectForm({ ...rectForm, color: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer border border-white/10 bg-transparent" />
                    <span className="text-white/40 text-xs">{rectForm.color}</span>
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {['#6366f1','#8b5cf6','#ec4899','#ef4444','#f59e0b','#22c55e','#06b6d4','#3b82f6','#64748b'].map(c => (
                      <button key={c} onClick={() => setRectForm({ ...rectForm, color: c })}
                        className={`w-5 h-5 rounded-sm border ${rectForm.color === c ? 'border-white scale-110' : 'border-white/10'} transition-transform`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                {pendingRect ? (
                  <div className="p-2 bg-violet-500/20 border border-violet-500/30 rounded-lg">
                    <p className="text-violet-400 text-xs">지도를 클릭하여 배치하세요</p>
                    <p className="text-white/30 text-[10px] mt-0.5">{pendingRect.label} ({pendingRect.widthMeter}m × {pendingRect.heightMeter}m)</p>
                    <button onClick={() => setPendingRect(null)} className="text-white/40 text-xs mt-1 hover:text-white">취소</button>
                  </div>
                ) : (
                  <button onClick={() => setPendingRect({ ...rectForm })}
                    className="w-full flex items-center justify-center gap-1 py-2 bg-violet-500 hover:bg-violet-600 text-white text-xs rounded-lg transition-colors">
                    지도를 클릭하여 배치
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 라벨 설정 패널 (오버레이) */}
        {showLabelPanel && !isViewOnly && (
          <div className="absolute top-0 left-0 w-64 h-full bg-[#1a1428]/95 backdrop-blur-sm border-r border-white/10 overflow-y-auto z-[500]">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">라벨 설정</h3>
                <button onClick={() => { setShowLabelPanel(false); setPendingLabel(null); }} className="text-white/30 hover:text-white/60 text-sm leading-none">✕</button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-white/40 text-[11px] mb-1">텍스트</label>
                  <textarea rows={2} value={labelForm.label} onChange={e => setLabelForm({ ...labelForm, label: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400 resize-y" />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-white/40 text-[11px] mb-1">가로 (m)</label>
                    <input type="number" value={labelForm.widthMeter} onChange={e => setLabelForm({ ...labelForm, widthMeter: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400" min="1" step="0.5" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-white/40 text-[11px] mb-1">세로 (m)</label>
                    <input type="number" value={labelForm.heightMeter} onChange={e => setLabelForm({ ...labelForm, heightMeter: e.target.value })}
                      className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-xs outline-none focus:border-violet-400" min="1" step="0.5" />
                  </div>
                </div>
                <div>
                  <label className="block text-white/40 text-[11px] mb-1">색상</label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={labelForm.color} onChange={e => setLabelForm({ ...labelForm, color: e.target.value })}
                      className="w-8 h-8 rounded cursor-pointer border border-white/10 bg-transparent" />
                    <span className="text-white/40 text-xs">{labelForm.color}</span>
                  </div>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {['#ffffff','#f4b8b8','#fbbf24','#34d399','#60a5fa','#a78bfa','#f472b6','#ef4444','#94a3b8'].map(c => (
                      <button key={c} onClick={() => setLabelForm({ ...labelForm, color: c })}
                        className={`w-5 h-5 rounded-sm border ${labelForm.color === c ? 'border-white scale-110' : 'border-white/10'} transition-transform`}
                        style={{ backgroundColor: c }} />
                    ))}
                  </div>
                </div>
                {pendingLabel ? (
                  <div className="p-2 bg-violet-500/20 border border-violet-500/30 rounded-lg">
                    <p className="text-violet-400 text-xs">지도를 클릭하여 배치하세요</p>
                    <p className="text-white/30 text-[10px] mt-0.5">"{pendingLabel.label}"</p>
                    <button onClick={() => setPendingLabel(null)} className="text-white/40 text-xs mt-1 hover:text-white">취소</button>
                  </div>
                ) : (
                  <button onClick={() => setPendingLabel({ ...labelForm })}
                    className="w-full flex items-center justify-center gap-1 py-2 bg-violet-500 hover:bg-violet-600 text-white text-xs rounded-lg transition-colors">
                    지도를 클릭하여 배치
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 그리기 설정 패널 (오버레이) */}
        {showDrawPanel && !isViewOnly && (
          <div className="absolute top-0 left-0 w-64 h-full bg-[#1a1428]/95 backdrop-blur-sm border-r border-white/10 overflow-y-auto z-[500]">
            <div className="p-3">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-white font-medium text-sm">그리기</h3>
                <button onClick={() => { setShowDrawPanel(false); setDrawMode(null); }} className="text-white/30 hover:text-white/60 text-sm leading-none">✕</button>
              </div>
              <div className="space-y-3">
                {/* 도구 선택 */}
                <div>
                  <label className="block text-white/40 text-[11px] mb-1">도구</label>
                  <div className="grid grid-cols-4 gap-1">
                    {[
                      { mode: 'pen', icon: <Paintbrush size={14} />, label: '펜' },
                      { mode: 'line', icon: <span className="text-sm font-bold">╱</span>, label: '직선' },
                      { mode: 'arrow', icon: <ArrowRight size={14} />, label: '화살표' },
                      { mode: 'dblArrow', icon: <span className="text-sm">↔</span>, label: '양쪽' },
                      { mode: 'rect', icon: <Square size={14} />, label: '사각형' },
                      { mode: 'circle', icon: <span className="text-sm">○</span>, label: '원' },
                      { mode: 'triangle', icon: <Triangle size={14} />, label: '삼각형' },
                      { mode: 'diamond', icon: <Diamond size={14} />, label: '마름모' },
                      { mode: 'star', icon: <Star size={14} />, label: '별' },
                      { mode: 'bubble', icon: <MessageCircle size={14} />, label: '말풍선' },
                      { mode: 'eraser', icon: <Eraser size={14} />, label: '지우개' },
                    ].map(t => (
                      <button key={t.mode} onClick={() => { setDrawMode(t.mode); setSelectedStrokeIdx(-1); setSelectedStrokeIndices(new Set()); }}
                        className={`flex flex-col items-center gap-0.5 py-2 rounded-lg text-[10px] font-medium transition-colors ${drawMode === t.mode ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:text-white'}`}>
                        {t.icon}
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
                {/* 색상 */}
                {drawMode && drawMode !== 'eraser' && drawMode !== 'move' && (
                  <div>
                    <label className="block text-white/40 text-[11px] mb-1">색상</label>
                    <div className="flex items-center gap-2">
                      <input type="color" value={drawColor} onChange={e => setDrawColor(e.target.value)}
                        className="w-8 h-8 rounded cursor-pointer border border-white/10 bg-transparent" />
                      <span className="text-white/40 text-xs">{drawColor}</span>
                    </div>
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {['#ff0000','#ff6b00','#fbbf24','#22c55e','#3b82f6','#8b5cf6','#ec4899','#ffffff','#000000'].map(c => (
                        <button key={c} onClick={() => setDrawColor(c)}
                          className={`w-5 h-5 rounded-sm border ${drawColor === c ? 'border-white scale-110' : 'border-white/10'} transition-transform`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                  </div>
                )}
                {/* 굵기 */}
                {drawMode && drawMode !== 'eraser' && drawMode !== 'move' && <div>
                  <label className="block text-white/40 text-[11px] mb-1">굵기: {drawWidth}px</label>
                  <input type="range" min="1" max="20" value={drawWidth} onChange={e => setDrawWidth(Number(e.target.value))}
                    className="w-full accent-violet-500" />
                  <div className="flex justify-between mt-1">
                    {[1, 3, 5, 10, 20].map(w => (
                      <button key={w} onClick={() => setDrawWidth(w)}
                        className={`px-2 py-1 text-[10px] rounded transition-colors ${drawWidth === w ? 'bg-violet-500/30 text-violet-300' : 'bg-white/5 text-white/40 hover:text-white'}`}>
                        {w}
                      </button>
                    ))}
                  </div>
                </div>}
                {/* 채우기 (도형 전용) */}
                {(['rect', 'circle', 'triangle', 'diamond', 'star', 'bubble'].includes(drawMode)) && (
                  <div className="flex items-center justify-between">
                    <label className="text-white/40 text-[11px]">채우기</label>
                    <button onClick={() => setDrawFill(v => !v)}
                      className={`px-3 py-1 text-[10px] rounded-md font-medium transition-colors ${drawFill ? 'bg-violet-500/30 text-violet-300' : 'bg-white/5 text-white/40'}`}>
                      {drawFill ? 'ON' : 'OFF'}
                    </button>
                  </div>
                )}
                {/* 전체 지우기 */}
                {strokes.length > 0 && (
                  <button onClick={() => setStrokes([])}
                    className="w-full flex items-center justify-center gap-1.5 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-xs rounded-lg transition-colors">
                    <Trash2 size={12} /> 전체 지우기 ({strokes.length}획)
                  </button>
                )}
                <p className="text-white/30 text-[10px]">
                  {drawMode === 'eraser' ? '지우개로 획을 터치하면 삭제됩니다' : drawMode === 'move' ? '획을 클릭하여 드래그로 이동하세요' : '지도 위에 마우스로 그리세요'}
                  <br />Ctrl+Z: 되돌리기 · Esc: 종료
                </p>
              </div>
            </div>
          </div>
        )}

        {/* 지도 + 로드뷰 */}
        <div className={`absolute inset-0 flex ${showRoadview ? 'flex-col lg:flex-row' : ''}`}>
          {/* 지도 영역 */}
          <div className={`relative overflow-hidden ${showRoadview ? 'flex-1' : 'w-full h-full'}`}
            onContextMenu={e => e.preventDefault()}>
            <div style={extraZoom !== 1 ? {
              position: 'relative', width: '100%', height: '100%',
              transform: `scale(${extraZoom})`,
              transformOrigin: 'center center',
            } : { position: 'relative', width: '100%', height: '100%' }}>
              <div ref={mapRef} className="w-full h-full" />
            </div>
            {/* 그리기 캔버스 — transform div 바깥, 지도 영역 위에 오버레이 */}
            <canvas ref={drawCanvasRef} className="absolute inset-0"
              style={{ zIndex: 450,
                pointerEvents: (drawMode || lassoMode || strokes.length > 0 || (!!selectedId && !isViewOnly)) ? 'auto' : 'none',
                cursor: lassoMode ? 'default' : drawMode === 'eraser' ? 'cell' : drawMode ? 'crosshair' : 'default' }} />
            {/* 장소 검색 */}
            <div className="absolute top-2 left-2 z-[490]">
              {showSearch ? (
                <div className="bg-black/80 backdrop-blur-sm rounded-lg shadow-lg w-72">
                  <form onSubmit={e => { e.preventDefault(); doPlaceSearch(); }} className="flex items-center gap-1.5 px-2.5 py-1.5">
                    <Search size={14} className="text-white/40 flex-shrink-0" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-white/30"
                      placeholder="장소 또는 주소 검색" autoFocus
                      onKeyDown={e => {
                        if (e.key === 'Escape') { e.preventDefault(); setShowSearch(false); setSearchResults([]); setSearchQuery(''); }
                      }} />
                    <button type="submit"
                      className="text-white/40 hover:text-white p-0.5" title="검색"><Search size={14} /></button>
                    <button type="button" onClick={() => { setShowSearch(false); setSearchResults([]); setSearchQuery(''); }}
                      className="text-white/30 hover:text-white p-0.5"><X size={14} /></button>
                  </form>
                  {searchResults.length > 0 && (
                    <div className="border-t border-white/10 max-h-48 overflow-y-auto">
                      {searchResults.map((r, i) => (
                        <button key={i} onClick={() => {
                          const map = mapInstanceRef.current;
                          if (map) {
                            setExtraZoom(1); // 검색 이동 시 줌 리셋
                            map.setCenter(new window.kakao.maps.LatLng(r.y, r.x));
                            map.setLevel(2);
                          }
                          setShowSearch(false); setSearchResults([]); setSearchQuery('');
                        }} className="w-full px-3 py-2 text-left hover:bg-white/10 transition-colors">
                          <div className="text-white text-xs font-medium truncate">{r.place_name}</div>
                          <div className="text-white/40 text-[10px] truncate">{r.address_name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <button onClick={() => setShowSearch(true)}
                  className="bg-black/70 backdrop-blur-sm rounded-lg px-2.5 py-1.5 text-white/60 hover:text-white text-xs flex items-center gap-1.5 shadow-lg transition-colors">
                  <Search size={14} /> 장소 검색
                </button>
              )}
            </div>
            <div className="absolute bottom-2 sm:bottom-4 left-2 sm:left-4 bg-black/70 backdrop-blur-sm rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 text-white/90 text-[10px] sm:text-xs z-[400] shadow-lg">
              레벨: {currentZoom}{extraZoom !== 1 ? ` (x${extraZoom})` : ''} | 아이템: {items.length}개
              {drawMode && <span className="ml-1 text-violet-400">| {{ pen: '✏️ 펜', line: '📏 직선', arrow: '→ 화살표', dblArrow: '↔ 양쪽화살표', rect: '▢ 사각형', circle: '○ 원', triangle: '△ 삼각형', diamond: '◇ 마름모', star: '☆ 별', bubble: '💬 말풍선', eraser: '🧹 지우개' }[drawMode]}</span>}
              {lassoMode && <span className="ml-1 text-blue-400">| 🔷 올가미</span>}
            </div>
            {!showRoadview && (
              <a href="https://pinkpaper.kr" target="_blank" rel="noopener noreferrer"
                className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 opacity-50 hover:opacity-90 transition-opacity z-[400]">
                <img src="/images/paper/logo_pinkpaper.png" alt="PinkPaper" className="h-5 sm:h-6" />
              </a>
            )}
            {/* 줌 컨트롤 */}
            <div className="absolute top-4 right-4 flex flex-col gap-1 z-[400]">
              <button onClick={() => {
                const map = mapInstanceRef.current;
                if (!map) return;
                if (extraZoom < 1) {
                  // 축소 상태: extraZoom을 1x 쪽으로
                  const idx = EXTRA_ZOOM_STEPS.indexOf(extraZoom);
                  if (idx >= 0 && idx < EXTRA_ZOOM_STEPS.length - 1) setExtraZoom(EXTRA_ZOOM_STEPS[idx + 1]);
                } else if (map.getLevel() > 1) {
                  map.setLevel(map.getLevel() - 1);
                } else {
                  // 카카오 최대 확대: extraZoom 증가
                  const idx = EXTRA_ZOOM_STEPS.indexOf(extraZoom);
                  if (idx >= 0 && idx < EXTRA_ZOOM_STEPS.length - 1) setExtraZoom(EXTRA_ZOOM_STEPS[idx + 1]);
                }
              }} className="w-8 h-8 bg-black/70 hover:bg-black/90 text-white rounded flex items-center justify-center" title="확대">
                <ZoomIn size={16} />
              </button>
              <button onClick={() => {
                const map = mapInstanceRef.current;
                if (!map) return;
                if (extraZoom > 1) {
                  // 확대 상태: extraZoom을 1x 쪽으로
                  const idx = EXTRA_ZOOM_STEPS.indexOf(extraZoom);
                  if (idx > 0) setExtraZoom(EXTRA_ZOOM_STEPS[idx - 1]);
                } else if (map.getLevel() < 14) {
                  map.setLevel(map.getLevel() + 1);
                } else {
                  // 카카오 최대 축소: extraZoom 감소
                  const idx = EXTRA_ZOOM_STEPS.indexOf(extraZoom);
                  if (idx > 0) setExtraZoom(EXTRA_ZOOM_STEPS[idx - 1]);
                }
              }} className="w-8 h-8 bg-black/70 hover:bg-black/90 text-white rounded flex items-center justify-center" title="축소">
                <ZoomOut size={16} />
              </button>
              {extraZoom !== 1 && (
                <button onClick={() => setExtraZoom(1)}
                  className={`w-8 h-auto py-1 ${extraZoom > 1 ? 'bg-violet-500/80 hover:bg-violet-500' : 'bg-blue-500/80 hover:bg-blue-500'} text-white text-[9px] rounded text-center`} title="원래 크기">
                  x1
                </button>
              )}
            </div>
          </div>

          {/* 로드뷰 영역 */}
          {showRoadview && (
            <div className="relative flex-1 border-t lg:border-t-0 lg:border-l border-white/10">
              <div ref={roadviewRef} className="w-full h-full" />
              {/* 로드뷰 헤더 */}
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-[400]">
                <div className="flex items-center gap-2 bg-black/70 backdrop-blur-sm rounded-lg px-3 py-1.5">
                  <ScanEye size={14} className="text-violet-400" />
                  <span className="text-white text-xs font-medium">로드뷰</span>
                  <span className="text-white/30 text-[10px] hidden sm:inline">· 지도 클릭으로 위치 이동</span>
                </div>
                <button onClick={() => setShowRoadview(false)}
                  className="w-7 h-7 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white/60 hover:text-white rounded-lg flex items-center justify-center transition-colors">
                  <X size={14} />
                </button>
              </div>
              {/* 로드뷰 로딩/에러 */}
              {!roadviewReady && !roadviewError && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0d0b14]/80 z-[399]">
                  <div className="flex items-center gap-2 text-white/60">
                    <Loader2 size={18} className="animate-spin" />
                    <span className="text-sm">로드뷰 로딩 중...</span>
                  </div>
                </div>
              )}
              {roadviewError && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#0d0b14]/80 z-[399]">
                  <div className="text-center">
                    <ScanEye size={32} className="text-white/20 mx-auto mb-2" />
                    <p className="text-white/60 text-sm">{roadviewError}</p>
                    <p className="text-white/30 text-xs mt-1">반경 100m 내 로드뷰가 없습니다</p>
                  </div>
                </div>
              )}
              {/* 로고 */}
              <a href="https://pinkpaper.kr" target="_blank" rel="noopener noreferrer"
                className="absolute bottom-2 sm:bottom-4 right-2 sm:right-4 opacity-50 hover:opacity-90 transition-opacity z-[400]">
                <img src="/images/paper/logo_pinkpaper.png" alt="PinkPaper" className="h-5 sm:h-6" />
              </a>
            </div>
          )}
        </div>

        {/* 속성 패널 (오버레이) */}
        {selectedItem && !isViewOnly && (
          <div className="absolute top-0 right-0 w-64 h-full bg-[#1a1428]/95 backdrop-blur-sm border-l border-white/10 overflow-y-auto z-[500]">
            <div className="p-3 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-white font-medium text-sm">속성</h3>
                <button onClick={() => setSelectedId(null)} className="text-white/30 hover:text-white/60 text-sm leading-none">✕</button>
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">종류</label>
                <p className="text-white text-sm">{selectedItem.type === 'asset' ? (selectedItem.asset?.name || '에셋') : selectedItem.type === 'rect' ? '영역' : '라벨'}</p>
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">이름 / 텍스트</label>
                <textarea rows={2} value={selectedItem.label || ''} onChange={e => updateSelected({ label: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400 resize-y" />
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">글자 크기 (px)</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="1" min="1" value={selectedItem.fontSize || ''}
                    onChange={e => updateSelected({ fontSize: e.target.value === '' ? null : (parseInt(e.target.value) || null) })}
                    placeholder="자동"
                    className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400" />
                  {selectedItem.fontSize && (
                    <button onClick={() => updateSelected({ fontSize: null })} className="px-2 py-1.5 bg-white/5 hover:bg-white/10 text-white/40 text-xs rounded">자동</button>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-white/40 text-xs mb-1">가로 (m)</label>
                  <input type="number" step="0.1" min="0.1" value={selectedItem.widthMeter ?? ''}
                    onChange={e => updateSelected({ widthMeter: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })}
                    onBlur={e => { if (!e.target.value || parseFloat(e.target.value) < 0.1) updateSelected({ widthMeter: 0.1 }); }}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400" />
                </div>
                <div>
                  <label className="block text-white/40 text-xs mb-1">세로 (m)</label>
                  <input type="number" step="0.1" min="0.1" value={selectedItem.heightMeter ?? ''}
                    onChange={e => updateSelected({ heightMeter: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0) })}
                    onBlur={e => { if (!e.target.value || parseFloat(e.target.value) < 0.1) updateSelected({ heightMeter: 0.1 }); }}
                    className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400" />
                </div>
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">회전 (도)</label>
                <div className="flex items-center gap-2">
                  <input type="number" step="5" value={selectedItem.rotation || 0}
                    onChange={e => updateSelected({ rotation: parseFloat(e.target.value) || 0 })}
                    className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400" />
                  <button onClick={() => rotateSelected(15)} className="p-1.5 bg-white/5 hover:bg-white/10 text-white/60 rounded"><RotateCw size={14} /></button>
                </div>
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">색상</label>
                <div className="flex items-center gap-2">
                  <input type="color" value={selectedItem.color || '#6366f1'} onChange={e => updateSelected({ color: e.target.value })}
                    className="w-8 h-8 rounded cursor-pointer bg-transparent border-0" />
                  <span className="text-white/40 text-xs">{selectedItem.color || '#6366f1'}</span>
                </div>
              </div>
              {/* 순서 (Z-Index) — 버튼 방식 */}
              <div>
                <label className="block text-white/40 text-xs mb-1">순서</label>
                <div className="grid grid-cols-4 gap-1">
                  <button onClick={() => moveZIndex('back')} className="flex items-center justify-center gap-0.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] rounded" title="맨 뒤로">
                    <ChevronsDown size={12} />
                  </button>
                  <button onClick={() => moveZIndex('down')} className="flex items-center justify-center gap-0.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] rounded" title="뒤로">
                    <ChevronDown size={12} />
                  </button>
                  <button onClick={() => moveZIndex('up')} className="flex items-center justify-center gap-0.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] rounded" title="앞으로">
                    <ChevronUp size={12} />
                  </button>
                  <button onClick={() => moveZIndex('front')} className="flex items-center justify-center gap-0.5 py-1.5 bg-white/5 hover:bg-white/10 text-white/60 text-[10px] rounded" title="맨 앞으로">
                    <ChevronsUp size={12} />
                  </button>
                </div>
                <div className="flex justify-between text-[9px] text-white/30 mt-0.5 px-1">
                  <span>맨뒤</span><span>뒤로</span><span>앞으로</span><span>맨앞</span>
                </div>
              </div>
              <div>
                <label className="block text-white/40 text-xs mb-1">그룹 ID</label>
                <input type="text" value={selectedItem.groupId || ''} onChange={e => updateSelected({ groupId: e.target.value || null })}
                  className="w-full bg-white/5 border border-white/10 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-violet-400" placeholder="같은 ID끼리 그룹" />
              </div>
              <div className="pt-2 border-t border-white/10 space-y-2">
                <button onClick={duplicateSelected} className="w-full flex items-center justify-center gap-1.5 py-2 bg-white/5 hover:bg-white/10 text-white/60 text-sm rounded-lg"><Copy size={14} /> 복제</button>
                <button onClick={deleteSelected} className="w-full flex items-center justify-center gap-1.5 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 text-sm rounded-lg"><Trash2 size={14} /> 삭제</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 히스토리 사이드바 */}
      {showHistory && (
        <div className="fixed top-0 right-0 w-80 h-full bg-[#1a1428] border-l border-white/10 z-[3000] flex flex-col shadow-2xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-white font-medium text-sm flex items-center gap-2"><Clock size={16} /> 히스토리</h3>
            <div className="flex items-center gap-1">
              {!shareId && historyList.length > 0 && !previewHistory && (
                <button onClick={deleteAllHistory} className="p-1 text-white/20 hover:text-red-400 transition-colors" title="전체 삭제"><Trash2 size={14} /></button>
              )}
              <button onClick={toggleHistory} className="p-1 text-white/40 hover:text-white"><X size={18} /></button>
            </div>
          </div>
          {previewHistory ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <div className="flex items-center gap-2.5 mb-2">
                  {previewHistory.savedByProfileImage
                    ? <img src={previewHistory.savedByProfileImage} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-violet-400/30" />
                    : <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${previewHistory.savedByType === 'guest' ? 'bg-amber-500/10 border-amber-400/30' : 'bg-violet-500/10 border-violet-400/30'}`}>
                        <User size={16} className={previewHistory.savedByType === 'guest' ? 'text-amber-400' : 'text-violet-400'} />
                      </div>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-white text-sm font-medium">{previewHistory.savedByName}</span>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${previewHistory.savedByType === 'guest' ? 'bg-amber-500/15 text-amber-400' : 'bg-violet-500/15 text-violet-400'}`}>
                        {previewHistory.savedByType === 'guest' ? '게스트' : '멤버'}
                      </span>
                    </div>
                    <div className="text-white/40 text-xs">{new Date(previewHistory.createdAt).toLocaleString('ko-KR')}</div>
                  </div>
                </div>
                {previewHistory.comment && (
                  <div className="px-3 py-2 bg-white/5 rounded-lg text-white/70 text-xs mb-3 italic">💬 {previewHistory.comment}</div>
                )}
                <div className="bg-white/5 rounded-lg px-3 py-2 text-white/50 text-[11px] mb-3">
                  오브젝트 {JSON.parse(previewHistory.items || '[]').length}개 · 그리기 {JSON.parse(previewHistory.drawings || '[]').length}개
                </div>
                <div className="text-center text-violet-400/60 text-[10px] mb-3">⬆ 지도에 이 시점이 표시되고 있습니다</div>
                <div className="flex gap-2">
                  <button onClick={exitPreview} className="flex-1 py-2 text-white/60 text-xs border border-white/10 rounded-lg hover:text-white hover:border-white/20">
                    ← 목록
                  </button>
                  {!isViewOnly && (
                    <button onClick={() => restoreHistory(previewHistory.id)}
                      className="flex-1 py-2 bg-violet-500 hover:bg-violet-600 text-white text-xs rounded-lg flex items-center justify-center gap-1 font-medium">
                      <RotateCcw size={12} /> 복원
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {historyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={20} className="animate-spin text-violet-400" />
                </div>
              ) : historyList.length === 0 ? (
                <div className="text-center py-12 text-white/40 text-sm">저장 기록이 없습니다</div>
              ) : (
                <div className="divide-y divide-white/[0.04]">
                  {historyList.map((h, idx) => (
                    <div key={h.id} className="group relative">
                      <button onClick={() => loadHistoryDetail(h.id)}
                        className="w-full px-4 py-3 text-left hover:bg-white/5 transition-colors">
                        <div className="flex items-center gap-2.5">
                          {h.savedByProfileImage
                            ? <img src={h.savedByProfileImage} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            : <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${h.savedByType === 'guest' ? 'bg-amber-500/10' : 'bg-violet-500/10'}`}>
                                <User size={14} className={h.savedByType === 'guest' ? 'text-amber-400/70' : 'text-violet-400/70'} />
                              </div>}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-white text-xs font-medium truncate">{h.savedByName}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${h.savedByType === 'guest' ? 'bg-amber-500/10 text-amber-400/80' : 'bg-violet-500/10 text-violet-400/80'}`}>
                                {h.savedByType === 'guest' ? '게스트' : '멤버'}
                              </span>
                              {idx === 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">최신</span>}
                            </div>
                            <div className="text-white/35 text-[11px] mt-0.5">
                              {new Date(h.createdAt).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </div>
                            {h.comment && <div className="text-white/50 text-[11px] mt-0.5 truncate">💬 {h.comment}</div>}
                          </div>
                        </div>
                      </button>
                      {!shareId && (
                        <button onClick={(e) => { e.stopPropagation(); deleteHistory(h.id); }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-white/0 group-hover:text-white/20 hover:!text-red-400 transition-colors rounded"
                          title="삭제">
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 저장 모달 (코멘트 + 게스트 이름) */}
      {showGuestNameModal && (
        <div className="fixed inset-0 bg-black/60 z-[4000] flex items-center justify-center p-4" onClick={() => setShowGuestNameModal(false)}>
          <div className="bg-[#1a1428] border border-white/10 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-medium text-sm mb-3 flex items-center gap-2"><Save size={16} /> 저장</h3>
            {/* 로그인 사용자 정보 / 게스트 이름 입력 */}
            {shareId ? (
              <div className="mb-3">
                <label className="block text-white/50 text-xs mb-1">이름 <span className="text-red-400">*</span></label>
                <input type="text" value={guestName} onChange={e => setGuestName(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                  placeholder="히스토리에 표시됩니다" />
              </div>
            ) : currentUser ? (
              <div className="flex items-center gap-2.5 mb-3 px-3 py-2 bg-white/5 rounded-lg">
                {currentUser.profileImage
                  ? <img src={currentUser.profileImage} alt="" className="w-8 h-8 rounded-full object-cover" />
                  : <div className="w-8 h-8 rounded-full bg-violet-500/15 flex items-center justify-center"><User size={14} className="text-violet-400" /></div>}
                <div>
                  <div className="text-white text-sm font-medium">{currentUser.name}</div>
                  <div className="text-white/30 text-[11px]">멤버로 저장됩니다</div>
                </div>
              </div>
            ) : null}
            <div className="mb-4">
              <label className="block text-white/50 text-xs mb-1">코멘트 <span className="text-white/20">(선택)</span></label>
              <input type="text" value={saveComment} onChange={e => setSaveComment(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-violet-400"
                placeholder="변경 내용 메모"
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (shareId && !guestName.trim()) return;
                    if (shareId) localStorage.setItem('layout-guest-name', guestName.trim());
                    setShowGuestNameModal(false);
                    doSave(false, guestName.trim() || undefined, saveComment.trim() || undefined);
                    setSaveComment('');
                  }
                }} />
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowGuestNameModal(false)}
                className="flex-1 py-2 text-white/60 text-sm border border-white/10 rounded-lg hover:text-white transition-colors">취소</button>
              <button onClick={() => {
                if (shareId && !guestName.trim()) return;
                if (shareId) localStorage.setItem('layout-guest-name', guestName.trim());
                setShowGuestNameModal(false);
                doSave(false, guestName.trim() || undefined, saveComment.trim() || undefined);
                setSaveComment('');
              }} className="flex-1 py-2 bg-violet-500 hover:bg-violet-600 text-white text-sm rounded-lg font-medium transition-colors">
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 컨텍스트 메뉴 백드롭: 닫기 클릭이 맵으로 전파되지 않도록 차단 */}
      {contextMenu.visible && (
        <div
          className="fixed inset-0 z-[4999]"
          onMouseDown={e => {
            e.stopPropagation();
            setContextMenu({ visible: false, x: 0, y: 0, itemId: null });
          }}
          onContextMenu={e => e.preventDefault()}
        />
      )}

      {/* 우클릭 컨텍스트 메뉴 */}
      {contextMenu.visible && (() => {
        const ctxItem = items.find(i => i._id === contextMenu.itemId);
        const closeMenu = () => setContextMenu({ visible: false, x: 0, y: 0, itemId: null });
        return (
          <div
            className="fixed z-[5000] bg-[#1a1428] border border-white/10 rounded-lg shadow-2xl py-1 min-w-[160px]"
            style={{ top: contextMenu.y, left: contextMenu.x }}
            onMouseLeave={closeMenu}
          >
            <button onClick={() => { duplicateSelected(); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-white/70 hover:bg-white/10 hover:text-white text-xs flex items-center gap-2">
              <Copy size={12} /> 복제 <span className="ml-auto text-white/20">Ctrl+C/V</span>
            </button>
            <div className="my-1 border-t border-white/10" />
            <button onClick={() => { moveZIndex('front'); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-white/70 hover:bg-white/10 hover:text-white text-xs flex items-center gap-2">
              <ChevronsUp size={12} /> 맨 앞으로
            </button>
            <button onClick={() => { moveZIndex('up'); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-white/70 hover:bg-white/10 hover:text-white text-xs flex items-center gap-2">
              <ChevronUp size={12} /> 앞으로
            </button>
            <button onClick={() => { moveZIndex('down'); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-white/70 hover:bg-white/10 hover:text-white text-xs flex items-center gap-2">
              <ChevronDown size={12} /> 뒤로
            </button>
            <button onClick={() => { moveZIndex('back'); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-white/70 hover:bg-white/10 hover:text-white text-xs flex items-center gap-2">
              <ChevronsDown size={12} /> 맨 뒤로
            </button>
            {selectedItemIds.size >= 2 && (<>
              <div className="my-1 border-t border-white/10" />
              <div className="px-3 py-1 text-[10px] text-white/30">정렬 ({selectedItemIds.size}개 선택)</div>
              <div className="grid grid-cols-3 gap-0.5 px-2 pb-1">
                <button onClick={() => { alignItems('left'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="왼쪽"><AlignStartVertical size={11} /></button>
                <button onClick={() => { alignItems('hcenter'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="가로중앙"><AlignCenterVertical size={11} /></button>
                <button onClick={() => { alignItems('right'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="오른쪽"><AlignEndVertical size={11} /></button>
                <button onClick={() => { alignItems('top'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="위쪽"><AlignLeft size={11} className="rotate-90" /></button>
                <button onClick={() => { alignItems('vcenter'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="세로중앙"><AlignCenter size={11} className="rotate-90" /></button>
                <button onClick={() => { alignItems('bottom'); closeMenu(); }} className="py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="아래쪽"><AlignRight size={11} className="rotate-90" /></button>
                <button onClick={() => { alignItems('hdist'); closeMenu(); }} className="col-span-3 py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="가로 간격 균등"><AlignHorizontalSpaceBetween size={11} /> 가로 간격 균등</button>
                <button onClick={() => { alignItems('vdist'); closeMenu(); }} className="col-span-3 py-1 text-white/60 hover:bg-white/10 hover:text-white text-[10px] rounded flex items-center justify-center gap-1" title="세로 간격 균등"><AlignVerticalSpaceBetween size={11} /> 세로 간격 균등</button>
              </div>
            </>)}
            <div className="my-1 border-t border-white/10" />
            <button onClick={() => { deleteSelected(); closeMenu(); }}
              className="w-full text-left px-3 py-1.5 text-red-400/80 hover:bg-red-500/10 hover:text-red-400 text-xs flex items-center gap-2">
              <Trash2 size={12} /> 삭제 <span className="ml-auto text-white/20">Del</span>
            </button>
          </div>
        );
      })()}

      {/* 단축키 */}
      {!isViewOnly && (
        <div className="px-2 sm:px-4 py-1.5 bg-[#1a1428] border-t border-white/10 flex items-center justify-between text-white/70 text-[10px] sm:text-[11px] z-[1000]">
          <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto">
            <span className="hidden sm:inline">R: 회전</span><span className="hidden sm:inline">방향키: 이동</span><span className="hidden sm:inline">Ctrl+C/V: 복제</span><span className="hidden sm:inline">Delete: 삭제</span><span className="hidden sm:inline">Ctrl+Z: 되돌리기</span><span className="hidden sm:inline">Ctrl+드래그: 다중선택</span><span className="hidden sm:inline">우클릭: 메뉴</span>
            <span className="sm:hidden">R:회전</span><span className="sm:hidden">방향키:이동</span><span className="sm:hidden">Del:삭제</span><span className="sm:hidden">⌘Z:되돌리기</span>
          </div>
          {lastSaved && <span className="text-white/50 flex-shrink-0 ml-2">저장 {lastSaved.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}</span>}
        </div>
      )}
    </div>
  );
}
